// @ts-nocheck — browser module; window globals & untyped DOM refs are expected
/**
 * Podstow — main view (index.html) entry point
 *
 * No bundler, no framework. Vanilla ES modules.
 *
 * Responsibilities of this file:
 *   1. Mount the shared auth UI (sign-in / sign-up / verify / forgot / reset).
 *   2. Toggle between the auth card and the lean main view based on auth state.
 *   3. Handle the OAuth callback (?code=) when the user returns from Cognito.
 *   4. Web Share Target / browser-extension ?url= handoffs: render the
 *      full-screen takeover overlay with pending / success / quota / error
 *      states, POST /ingest, and dismiss back to the source app via the
 *      multi-strategy dismiss() helper.  Logged-out shares (and mid-flow
 *      401s) bypass the takeover and land on the auth card with a contextual
 *      header instead.
 *   5. Lean main view — usage warning strip, paste-and-queue card with inline
 *      state transform, the queue/player, and the new-user "Try a sample"
 *      nudge (ADR #2089 §4).
 *   6. PWA install banner.
 *   7. Android TWA postMessage bridge — keeps the native shell's stored
 *      credentials in sync with the SPA, and dispatches close messages from
 *      the dismiss helper.
 *   8. Service worker registration.
 *
 * Settings (subscription, feed URL, voices, MCP, connected apps, feedback,
 * sign out) live on /settings and are owned by settings.js.
 */

import { sendUrl, sendText, sendShare, extractUrls, apiRequest, _setRetryDelays, configureClient, httpRef, PLAN_LIMIT_MESSAGE, THROTTLED_MESSAGE } from './shared/api.js';
import { showToast, _setToastStack } from './shared/toast.js';
import { shareLink, episodeShareData } from './shared/share-link.js';
import { renderSaveStatus } from './shared/save-status.js';
import { createQueueHost, httpQueueApi } from './queue-host.js';
import { createAudioTrace } from './audio-trace.js';
import {
  emitEvent,
  beginShare,
  recordAttributedSignup,
  shouldAttributeSignup,
  recordPlaybackActivation,
} from './shared/analytics.js';
import { createEpisodePanel, episodeDetailsFromHref } from './episode-panel.js';
import { createTranscriptCache, loadTranscript, cachedCues, cachedTranscript } from './shared/transcript-fetch.js';
import {
  submitProblemReport, reportSubmittedMessage, composeFailedEpisodeNotes,
  REPORT_HELP_TEXT, REPORT_NOTES_PLACEHOLDER,
} from './shared/problem-report.js';
import { isLoggedIn, isReturningUser, getToken, peekAccessToken, onAuthChange, clearLocalSession, isAdmin,
         getUserSub, pushConfigToWebkit, hasWebkitConfigHandler, buildNativeConfigPayload } from './shared/auth.js';
import { safeStorage } from './shared/safe-storage.js';
import { composeQuotaMessage, deriveUsage, formatPeriodReset, formatResetInstant } from './shared/usage.js';
import { selectAuthWelcome } from './shared/onboarding/welcome.js';
import { getDesktopBrowser } from './shared/onboarding/apps.js';
import { recommendedInstall, installDismissKey } from './shared/install-prompt.js';
import { DEMO_ARTICLE } from './shared/onboarding/demo-article.js';
import { getExtensionMarker, CLOSE_WINDOW_EVENT, EXT_PRESENT_EVENT, OPEN_APP_EVENTS, QUEUE_UPDATED_EVENT } from './shared/ext-presence.js';
import { hasAppleIapBridge } from './shared/apple-iap.js';

import { parseInstallSignal } from './shared/client-install.js';
import { initAuthUI, configureAuthUI, processOAuthCallback,
         setAuthHeader, showAuthPanel, validatePassword,
         handleSignUp, handleVerifyCode, handleResendCode,
         startResendCooldown, handleForgotPassword, handleResetPassword,
         handleResendResetCode, handleSignInSubmit,
         _clearResendTimer, _clearResendResetTimer,
         _resetSignUpState, _resetResetState } from './shared/auth-ui.js';
import { SS_NEXT_REDIRECT } from './shared/oauth-return.js';
import { markTwaSession, markTwaFromReferrer, isTwaSessionMarked } from './shared/twa-marker.js';
// Whether this document can sell a pack — shared with settings.js, which
// renders the card from the same answer, so the wall never offers a route
// Settings has no control for (#3416).
import { hasPackDoor, warmPackDoor, resetPackDoor, onPackDoorOpen } from './shell.js';
import { refreshOnResume } from './account-refresh.js';
// Diagnosis only, for the admin bridge debug panel — never a gate. shell.js
// above runs the probe this reads back (once per document, for the pack door).
import {
  describePlayBilling, onPlayBillingDiagnosis, SERVICE_TIMEOUT_MS,
} from './shared/google-play-billing.js';

// ---------------------------------------------------------------------------
// Client install prompt (ADR #1679)
//
// One banner that offers the single podstow client worth installing on this
// surface, decided by the shared `recommendedInstall` resolver (PWA on
// ChromeOS, the native app on Android, the browser extension on desktop
// Chromium/Firefox, nothing elsewhere). The banner is a thin renderer of the
// resolver result — only the copy, CTA and install action vary per client; all
// the gating lives in the resolver. The surface→client decision and install
// gating live ONLY here and in the resolver; the single-affordance arch check
// enforces that.
// ---------------------------------------------------------------------------
let deferredInstallPrompt = null;

// The native Android app's "installed here" signal (#1644): resolved once,
// best-effort, from `navigator.getInstalledRelatedApps()` (Chromium-only;
// needs the manifest `related_applications` entry + the digital-asset-links
// association, both in place). Starts false so the Android nudge shows until we
// learn otherwise; refreshNativeAppInstalled() flips it and re-renders to hide
// the nudge when the app turns out to be installed. Left false on every browser
// that lacks the API — the nudge still shows, matching how the extension nudge
// tolerates an unknowable install state until its presence marker arrives.
let _nativeAppInstalled = false;
let _nativeAppInstalledChecked = false;

// The Play package the related-apps API reports when the native app is present.
const ANDROID_APP_ID = 'app.podstow.android';

// Best-effort, one-shot probe of getInstalledRelatedApps(). Fire-and-forget from
// showInstallBanner; only re-renders when it learns the app IS installed (to
// pull the nudge), so a false result never causes an extra flash.
async function refreshNativeAppInstalled() {
  if (_nativeAppInstalledChecked) return;
  _nativeAppInstalledChecked = true;
  const getApps = typeof navigator !== 'undefined' && navigator.getInstalledRelatedApps;
  if (typeof getApps !== 'function') return; // non-Chromium / unsupported
  try {
    const apps = await getApps.call(navigator);
    if (apps.some((a) => a && (a.id === ANDROID_APP_ID || a.platform === 'play'))) {
      _nativeAppInstalled = true;
      showInstallBanner();
    }
  } catch { /* best-effort suppression signal — ignore failures */ }
}

// The install-prompt audience (ADR #1679 §3, amended): an authenticated,
// established user (saved ≥ 1 article — a non-empty /me `clients` map, #2093)
// who is set up to listen (/me `setUpToListen` — completed an in-app episode OR
// subscribed in a podcast app, #2199/#2200). Both start false so a brand-new
// user (and the pre-/me window) never sees the banner; fetchAccountInfo sets
// them from /me, and a first in-session save latches `_established`. Deliberately
// decoupled from podcast-app subscription — a returning non-subscriber still
// qualifies once they've listened in-app. Holding the prompt until the user has
// experienced the save → listen loop avoids overloading them mid-discovery.
let _established = false;
let _setUpToListen = false;

// Reset the per-account UI state that must never leak from one account to the
// next on a shared device or an in-place sign-in (no reload): the install-banner
// audience (`_established`, `_setUpToListen`, re-earned via the new account's own
// /me — #2093 / #1680) and the value-first install-how-to funnel's first-save
// state (`_hasSavedArticle`, so a stale `true` can't defeat a brand-new user's
// deferral). Called from every account-teardown site (logout, onSignedIn, the
// OAuth beforeCallback) so a new per-account flag only has to be dropped here.
//
// The pack door is one of those flags since it became tier-priced (#3817): it
// latches "the biller priced a pack THIS TIER can buy", so a door proven for
// the previous account's tier says nothing about this one, and carrying it
// over would point a wall at a card /settings will not render (#3416). The
// re-warm on the new account's /me puts it back wherever the door is open.
function resetPerAccountState() {
  _established = false;
  _setUpToListen = false;
  _hasSavedArticle = null;
  resetPackDoor();
}

// The recommendation currently rendered, so the install / dismiss handlers know
// which client they're acting on (its install action and per-client dismiss
// key). Null when the banner is hidden.
let _activeInstall = null;

// Where the SPA serves the shared store-badge artwork from —
// clients/web/public/shared/badges is a symlink to clients/shared/badges (same
// path Settings uses). The Android nudge renders Google's own badge from here.
const INSTALL_BADGE_BASE = '/shared/badges/';

// The legacy global dismiss key from the PWA-only banner era (#2093). Honoured
// as the PWA's dismiss so a user who already dismissed it isn't re-nagged; new
// permanent dismissals write the per-client key (ADR #1679 §3).
const LS_LEGACY_INSTALL_DISMISSED = 'podstow_install_dismissed';

// Session-scoped suppression keyed by client id. Set when the user clicks
// through to a client's store (we can't observe whether they finished the
// install) or when an install signal proves they just got it — so the prompt
// doesn't re-nag for the rest of the session. NOT permanent: a later session
// re-offers if the client still isn't installed (an actual install is then
// detected via the data-podstow-ext marker; the ✕ button is the only PERMANENT
// dismiss). #1680.
const SS_INSTALL_SUPPRESS = 'podstow_install_suppressed';

// Whether to withhold this client's prompt: a permanent ✕ dismiss (per-client,
// or the legacy global for the PWA) OR a session-scoped suppression from a
// store click-through / detected install.
function installSuppressed(client) {
  if (safeStorage.getItem(installDismissKey(client))) return true;
  if (client === 'pwa' && safeStorage.getItem(LS_LEGACY_INSTALL_DISMISSED)) return true;
  return sessionStorage.getItem(SS_INSTALL_SUPPRESS) === client;
}

function showInstallBanner() {
  const banner = document.getElementById('install-banner');
  if (!banner) return;
  // Don't prompt before sign-in — the user doesn't yet know what podstow is, and
  // the resolver's audience gate assumes an authenticated user. Re-checked from
  // updateAuthUI on login and from fetchAccountInfo once /me resolves.
  const loggedIn = isLoggedIn();
  // Kick off the one-shot native-app probe (Android only bothers to read the
  // result). Fire-and-forget: it re-renders if it finds the app installed.
  if (loggedIn) refreshNativeAppInstalled();
  const rec = loggedIn
    ? recommendedInstall({
        userAgent: navigator.userAgent,
        userAgentData: navigator.userAgentData,
        maxTouchPoints: navigator.maxTouchPoints,
        established: _established,
        setUpToListen: _setUpToListen,
        // beforeinstallprompt only fires when the PWA isn't already installed —
        // the resolver's "PWA available ∧ not installed here" signal (§2).
        canPromptPwa: Boolean(deferredInstallPrompt),
        extensionPresent: Boolean(getExtensionMarker(document)),
        // The native app's "installed here" signal, per platform (#1644). Each
        // term proves the same thing — this device already has the app — and the
        // resolver only consults it inside the branch that platform reaches.
        //
        // Running inside a native shell is definitive proof: without it the SPA
        // would nag a user to "get the app" they're already using. The TWA
        // bridge covers Android (getInstalledRelatedApps() doesn't reliably
        // report the wrapping app from inside the TWA); the StoreKit bridge
        // covers the Apple shell, and is the ONLY positive signal there — iOS
        // has no getInstalledRelatedApps() equivalent, so in plain Safari an
        // owner of the app reads as "not installed" and sees the nudge until
        // they dismiss it.
        nativeAppInstalled: _nativeAppInstalled || isTwaSessionMarked() || hasAppleIapBridge(),

        isDismissed: installSuppressed,
      })
    : null;
  if (!rec) { hideInstallBanner(); return; }
  _activeInstall = rec;
  const copy = banner.querySelector('.install-banner-copy');
  if (copy) copy.textContent = rec.copy;
  const btn = document.getElementById('install-btn');
  if (btn) renderInstallCta(btn, rec);
  banner.style.display = 'flex';
  document.body.classList.add('has-install-banner');
  // Reserve exactly the banner's rendered height. The copy wraps to a variable
  // number of lines, so the layout offsets (body padding-top, the pinned header
  // and paste-card) read this instead of a hard-coded guess that under-shoots
  // and lets the banner obscure the top of the content.
  syncInstallBannerHeight();
}

// Render the CTA into #install-btn. Most clients get a plain text button; the
// native nudges get the store's own official badge instead (ADR #1679) — Google
// Play on Android, the App Store on iOS. It's the recognised trust signal a
// generic button can't convey, and honest here since the tap opens that
// listing. The badge is the store's trademarked artwork, only ever scaled
// (never recoloured — each store's brand rules; see the badges README), so its
// `.btn-install--badge` styling strips the amber text-button chrome down to a
// bare wrapper. The button is reused across surfaces, so the text branch clears
// any badge left from a prior native render.
function renderInstallCta(btn, rec) {
  if (rec.badge) {
    btn.classList.add('btn-install--badge');
    // The artwork basename (sans extension) — drives the per-badge clear-space
    // inset in CSS, since each store bakes a different amount (same data-badge
    // hook Settings uses in buildStoreBadge).
    btn.dataset.badge = rec.badge.file.replace(/\.[^.]+$/, '');
    const img = document.createElement('img');
    img.src = `${INSTALL_BADGE_BASE}${rec.badge.file}`;
    // Intrinsic dimensions reserve the box (CLS); CSS sets the rendered height,
    // preserving the badge's aspect ratio. The badge carries its call to action
    // as artwork ("Get it on Google Play", "Download on the App Store"), so its
    // alt is the button's accessible name — which is why each badge's `alt`
    // reads as an action rather than a destination.
    img.width = rec.badge.width;
    img.height = rec.badge.height;
    img.alt = rec.badge.alt;
    img.decoding = 'async';
    btn.textContent = '';
    btn.appendChild(img);
    return;
  }
  btn.classList.remove('btn-install--badge');
  delete btn.dataset.badge;
  btn.textContent = rec.cta;
}

// Publish the fixed banner's rendered height so CSS can reserve space for it
// (see `body.has-install-banner` in style.css). Re-run whenever the wrap can
// change — on show and on viewport resize / rotate.
function syncInstallBannerHeight() {
  const banner = document.getElementById('install-banner');
  if (!banner || banner.style.display === 'none') return;
  document.documentElement.style.setProperty('--install-banner-h', `${banner.offsetHeight}px`);
}

function hideInstallBanner() {
  _activeInstall = null;
  const banner = document.getElementById('install-banner');
  if (!banner) return;
  banner.style.display = 'none';
  document.body.classList.remove('has-install-banner');
  document.documentElement.style.removeProperty('--install-banner-h');
}

window.addEventListener('resize', syncInstallBannerHeight);

// The ✕ button: a permanent per-client dismiss (ADR #1679 §3) — the user's
// explicit "don't offer this again". Reads `_activeInstall` before
// hideInstallBanner clears it. (A store click-through only session-suppresses,
// and an actual install is detected separately — #1680.)
function dismissInstall() {
  if (_activeInstall) safeStorage.setItem(installDismissKey(_activeInstall.client), '1');
  hideInstallBanner();
}

// Confirming we're running inside our own Android app writes the SAME permanent
// latch the ✕ does (#banner-inside-the-app). Being in the app is the strongest
// proof there is that the user has it, and every *live* in-app signal is
// fragile: getInstalledRelatedApps() doesn't report the wrapping TWA, the CCT
// handshake dies across a background-return and isn't always re-delivered, and
// document.referrer carries our package only on the launch navigation. A load
// that catches none of them nags the user to install the app they are looking
// at. Latching on the first confirmation means a later load doesn't have to
// catch any of them: localStorage answers instead.
//
// It leaves one hole, and a permanent one: someone who uninstalls the app and
// comes back on mobile web is never re-offered it. The latch does NOT go with
// the app — a TWA renders in the user's Chrome, so this localStorage is Chrome's
// copy of our origin (the same profile-wide scope twa-marker.js cites as its
// reason for keeping the per-tab marker in sessionStorage), and an uninstall
// leaves it untouched. Nor can a later load clear it: an empty
// getInstalledRelatedApps() is precisely the reading we already know lies inside
// the TWA, so there is no trustworthy "the app is gone" signal to clear it on.
// Accepted deliberately — nagging a user inside the app is the louder failure —
// but it is a one-way door, not a self-healing one.
function latchAndroidInstalled() {
  safeStorage.setItem(installDismissKey('android'), '1');
}

// The one route by which THIS page confirms the Android TWA bridge: stamp the
// per-tab marker AND latch the nudge off for good. Every confirmation site in
// app.js calls this rather than markTwaSession(), so none of them can confirm
// without latching. settings.js confirms the same bridge and does NOT latch —
// harmless only because it is reached by an in-app navigation, whose tab the
// launch referrer already latched.
function confirmTwaSession() {
  markTwaSession();
  latchAndroidInstalled();
}

// The launch-referrer half of the same thing. Marks (and so latches) only when
// the referrer is one of OUR package ids — a third-party Custom Tab leaves ITS
// package there, and must never latch our own nudge off.
function confirmTwaFromReferrer(referrer) {
  if (!markTwaFromReferrer(referrer)) return false;
  latchAndroidInstalled();
  return true;
}

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredInstallPrompt = e;
  showInstallBanner();
});

window.addEventListener('appinstalled', () => {
  deferredInstallPrompt = null;
  hideInstallBanner();
});

// The extension's content script stamps its presence marker and fires
// EXT_PRESENT_EVENT at document_idle — possibly after the banner has already
// rendered. Re-evaluate so a desktop user who already has the extension isn't
// left looking at an "install the extension" prompt: with the marker now set,
// the resolver treats the extension as installed here and returns null (§2/§5).
window.addEventListener(EXT_PRESENT_EVENT, () => { showInstallBanner(); });

// ---------------------------------------------------------------------------
// TWA postMessage bridge — syncs config to Android SharedPreferences
//
// Chrome Custom Tabs postMessage channel uses a MessagePort pair:
//   1. requestPostMessageChannel() triggers Chrome to create a port pair
//   2. Chrome sends port[1] to the page via postMessageToMainFrame —
//      arrives as a window "message" event with event.ports[0]
//   3. Native → web messages flow through the port (port[0].postMessage
//      on Chrome's side → port[1].onmessage on the page)
//   4. Web → native replies go through port[1].postMessage
//
// Earlier code tried to use event.source.postMessage() which is null for
// Custom Tabs messages (they don't originate from another browsing context).
// ---------------------------------------------------------------------------
let _twaPort = null;

// ---------------------------------------------------------------------------
// Bridge debug panels — shown only to admin users
//
// The dbg* helpers are declared here (before the early-port adoption block
// below) because that block can run at module-evaluation time on Android TWA,
// when the inline <head> script has already captured the port.  It calls
// dbgPortSrc/dbgLog, which touch _dbgLogLines / _dbgPortCount — declaring them
// later would put them in the TDZ and halt module evaluation.
// ---------------------------------------------------------------------------

// Admin-only: bridge debug panel visibility is user-controllable via a
// toggle in /settings (key set there). Absent value = default-on,
// matching the panel's original always-on behaviour.
const LS_BRIDGE_DEBUG_VISIBLE = 'podstow_bridge_debug_visible';
function isBridgeDebugVisible() {
  const stored = safeStorage.getItem(LS_BRIDGE_DEBUG_VISIBLE);
  return stored === null ? true : stored === '1';
}

// Admin-only audio trace (audio-trace.js): an overlay over the player listing what
// its <audio> element and engine do, stamped to the ms, for screen recordings of
// playback glitches. Shown and hidden with the bridge debug panel; it records from
// the first play either way, so a late reveal still shows the history.
const audioTrace = createAudioTrace();
function syncAudioTrace(show) {
  const overlay = document.getElementById('audio-trace');
  if (!overlay) return;
  overlay.hidden = !show;
  if (show) audioTrace.show(document.getElementById('audio-trace-log'));
  else audioTrace.hide();
}

const DBG_LOG_MAX = 10;
const _dbgLogLines = [];
let _dbgPortCount = 0;

function dbgLog(msg) {
  const ts = new Date().toISOString().slice(11, 23); // HH:mm:ss.SSS
  _dbgLogLines.push(`${ts}  ${msg}`);
  if (_dbgLogLines.length > DBG_LOG_MAX) _dbgLogLines.shift();
  const el = document.getElementById('dbg-bridge-log');
  if (el) el.textContent = _dbgLogLines.join('\n');
}

// A redacted one-line summary of the setConfig bundle the SPA pushes to the
// native (WKWebView) wrapper — lengths / presence only, never the secret
// values. Its job is to make an iOS share dead-end diagnosable from the web
// panel (no native rebuild): the Share Extension reads these exact fields from
// the shared Keychain, and a present `accessToken` with an absent
// `tokenEndpoint`/`clientId` means it can read the token but can't refresh it
// once it expires — which surfaces as 401 → needsReauth → the "open podstow to
// continue" handoff. `exp` says whether the token the extension would read is
// already stale (forcing that refresh). Pure (takes the payload) so it stays
// testable and can't accidentally log a raw credential.
function redactConfigSummary(p) {
  const expMs = (Number(p.expiresAt) || 0) * 1000; // payload carries epoch SECONDS
  const now = Date.now();
  const exp = !expMs ? 'none'
    : expMs <= now ? `expired ${Math.round((now - expMs) / 60000)}m`
      : `${Math.round((expMs - now) / 60000)}m`;
  return `at=${p.accessToken ? String(p.accessToken).length : 'none'}`
    + ` rt=${p.refreshToken ? 'y' : 'n'}`
    + ` te=${p.tokenEndpoint ? 'y' : 'n'}`
    + ` cid=${p.clientId ? 'y' : 'n'}`
    + ` exp=${exp}`
    + ` api=${p.apiUrl ? 'y' : 'n'}`
    // Which way the Android bridge-diagnostics beacon gate is set
    // (BridgeDiagnostics.kt) — a silent beacon is undiagnosable without it.
    + ` adm=${p.isAdmin ? 'y' : 'n'}`;
}

// Summarise what a setConfig push would carry right now — reads the same
// builder the push uses so the panel reflects exactly what the extension
// receives (the bare origin is the iOS transport's apiUrl; see
// buildNativeConfigPayload).
function dbgConfigSummary() {
  return redactConfigSummary(buildNativeConfigPayload(window.location.origin));
}

function dbgUpdateConfig() {
  const el = document.getElementById('dbg-bridge-token');
  if (el) el.textContent = dbgConfigSummary();
}

function dbgUpdateChannel() {
  const el = document.getElementById('dbg-bridge-status');
  if (el) el.textContent = (_twaPort || getExtensionMarker(document)) ? 'connected' : 'null';
}

// The Play Digital Goods probe, in one line (#3043, ADR #3812).
//
// The purchase card keys on `getDigitalGoodsService` resolving AND a
// `getDetails()` that priced something, every failing arm collapses to the
// same hidden card, and a free account is shown no note in its place
// (docs/constraints/billing-store-cards.md) — so a Play-installed,
// billing-capable build with an empty Subscription section looks exactly like
// one missing the whole shell wiring. This row is where that stops being a
// silence: it names WHICH arm, so the reader goes to the Play Console or to
// the device rather than to the manifest by elimination.
//
// Read-only. `describePlayBilling` is a report and gates nothing — the probe
// that fills it is shell.js's, run once per document for the pack door, so
// this row observes the same answer the cards did rather than asking Play
// again. It carries no amount: which rungs Play priced is the finding, and the
// number is FF-2's to render.
//
// The IDS are whichever set `getDetails` was last asked for, which on this
// page is the pack warm-up's rather than the tier ladder's — the plan cards
// live on /settings and never run here. That is the same door either way: one
// service, one catalogue call, so packs priced at nothing is tiers priced at
// nothing. The `service:` half above is the part that is surface-independent.
function dbgPlayBillingSummary(d) {
  if (d.api === 'absent') return 'no Digital Goods API';
  if (d.service === 'unprobed') return 'probing…';
  if (d.service === 'timeout') return `service: timeout (${SERVICE_TIMEOUT_MS}ms)`;
  if (d.service === 'threw') return 'service: threw';
  if (d.service !== 'resolved') return 'service: none';
  if (d.details === 'unasked') return 'service: ok';
  // Whether the retry was needed is worth as much as whether it worked: a card
  // that only paints on the second ask is one whose first ask Play refused.
  //
  // The ELAPSED time is what turns the verdict into a measurement. "timeout"
  // alone cannot say whether the bound was the fault — a catalogue priced at
  // eight seconds says it was, one still silent at sixty says Play was — and
  // that ambiguity is what left the 1.1.6879 report unresolved through two
  // rounds of fixes aimed at the wrong half.
  const retried = d.attempts > 1 ? ' (retried)' : '';
  const took = d.elapsedMs == null ? '' : ` ${(d.elapsedMs / 1000).toFixed(1)}s`;
  if (d.details !== 'answered') return `service: ok · getDetails: ${d.details}${took}${retried}`;
  const total = d.priced.length + d.unpriced.length;
  return `service: ok · priced ${d.priced.length}/${total}${took}${retried}`;
}

/** The last unpriced line written, so a refresh cannot log it twice. */
let _dbgPlayUnpricedLogged = null;

function dbgUpdatePlayBilling() {
  const el = document.getElementById('dbg-play-billing');
  if (!el) return;
  const diagnosis = describePlayBilling();
  el.textContent = dbgPlayBillingSummary(diagnosis);
  // The ids go to the log rather than the row: an unpriced rung is the
  // actionable half (it is a Play Console fix, not a code one) and the product
  // ids are far too long to sit in a definition list.
  //
  // Logged on CHANGE only. This runs on every reveal and every extension
  // announce, the log holds ten lines, and a repeat would evict the bridge
  // handshake history — which is the one thing an admin signing in mid-load
  // cannot get back by looking again.
  const line = diagnosis.unpriced.length ? `play: unpriced ${diagnosis.unpriced.join(' ')}` : '';
  if (line && line !== _dbgPlayUnpricedLogged) dbgLog(line);
  _dbgPlayUnpricedLogged = line;
}

// Follow the probe rather than sampling it. Every `dbgDetectBridge` a TWA gets
// has fired before Play has answered: the early-port block runs at module
// evaluation, the boot-time reveal a tick later, and syncBridgeDebugPanel's
// transition guard swallows the auth-change and pageshow calls after that. The
// service arm is bounded at three seconds and `getDetails` lands after that
// again, so a row written once would read "probing…" for the life of the
// document — and "priced 0/2", the #3812 case the row exists to name, is the
// last arm to settle and so the one that would never be shown.
onPlayBillingDiagnosis(dbgUpdatePlayBilling);

function dbgPortSrc(how, eventSource) {
  _dbgPortCount++;
  const src = eventSource === undefined ? '' : ` src=${eventSource === null ? 'null' : eventSource === window ? 'self' : 'other'}`;
  dbgLog(`port: ${how}${src} #${_dbgPortCount}`);
}

function dbgWmsg(event) {
  const src = event.source === null ? 'null' : event.source === window ? 'self' : 'other';
  const ports = event.ports ? event.ports.length : 0;
  const origin = event.origin === '' ? '∅' : (event.origin || '?');
  const data = typeof event.data === 'string'
    ? event.data.slice(0, 40)
    : JSON.stringify(event.data).slice(0, 40);
  dbgLog(`wmsg: src=${src} ports=${ports} o=${origin} ${data}`);
}

function dbgRx(type) {
  dbgLog(`rx: ${type}`);
}

function dbgTx(type) {
  dbgLog(`tx: ${type}`);
}

function dbgDetectBridge() {
  const extMarker = getExtensionMarker(document);
  const hasChrome = typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.id;
  const hasBrowser = typeof browser !== 'undefined' && browser.runtime;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const hasWebKit = hasWebkitConfigHandler();

  let bridge = 'none';
  if (_twaPort) bridge = 'android (TWA)';
  // The Apple wrapper registers the same webkit handler on iOS and macOS
  // (clients/apple/Podstow/ContentView.swift), and JS can't tell them apart
  // from it — so label by capability, not a guessed platform.
  else if (hasWebKit) bridge = 'WKWebView';
  // The content-script marker is the authoritative extension signal — the
  // chrome/browser global checks below never fire from page context in
  // Firefox and need externally_connectable (unset) to be reliable in Chrome.
  else if (extMarker) bridge = extMarker;
  else if (hasBrowser && isSafari) bridge = 'safari ext';
  else if (hasChrome) bridge = 'chrome ext';
  else if (hasBrowser) bridge = 'browser ext';

  const elActive = document.getElementById('dbg-bridge-active');
  if (elActive) elActive.textContent = bridge;
  // Keep the token row current whenever the bridge is (re)detected — for a
  // WKWebView host it's the field-by-field state the Share Extension will read.
  dbgUpdateConfig();

  dbgUpdatePlayBilling();

  dbgLog(`bridge: ${bridge}`);
  if (extMarker) dbgLog(`ext: announced ${extMarker}`);
  else if (hasChrome || hasBrowser) dbgLog(`ext-runtime: ${hasChrome ? 'chrome' : 'browser'}`);
  if (hasWebKit) dbgLog('webkit: handler available');
  if (hasBrowser && isSafari) {
    const hasNative = typeof browser.runtime.sendNativeMessage === 'function';
    dbgLog(`safari: native messaging ${hasNative ? 'available' : 'none'}`);
  }
}

// Admin-only bridge debug panel visibility. Re-run on every auth change, not
// only at boot: isAdmin() reads the ID token, which doesn't exist while the
// sign-in card is up — a boot-time-only gate left the panel hidden for the
// whole session when the admin signed in on that very page load, exactly the
// load whose bridge handshake (the token push to the native shell) most needs
// observing. dbgLog has been recording into the (hidden) panel elements all
// along, so a late reveal still shows the boot history.
function syncBridgeDebugPanel() {
  const dbgPanel = document.getElementById('bridge-debug');
  if (!dbgPanel) return;
  const show = isAdmin() && isBridgeDebugVisible();
  syncAudioTrace(show);
  if (show === !dbgPanel.hidden) return; // no transition (incl. token refreshes)
  dbgPanel.hidden = !show;
  // Freshly revealed: bring the status rows up to date — while hidden they
  // were only maintained opportunistically by the bridge event handlers.
  if (show) {
    dbgUpdateChannel();
    dbgDetectBridge();   // refreshes the Play row too
  }
}
onAuthChange(syncBridgeDebugPanel);

// Re-detect when the extension content script announces itself. It runs at
// document_idle, so it can land before or after boot — and an admin may sign
// in later in the load — so this is registered at module level and outside
// the admin gate; the dbg helpers are no-op cheap while the panel is hidden.
window.addEventListener(EXT_PRESENT_EVENT, () => {
  dbgDetectBridge();
  dbgUpdateChannel();
});

// Load-time TWA signal (#banner-inside-the-app): our own Android app launches
// the SPA as a Trusted Web Activity, which stamps document.referrer with our
// package id.  Confirm the tab straight away so the "get the app" nudge is
// suppressed on a cold app launch whose CCT postMessage handshake arrives late
// or never re-fires — the case getInstalledRelatedApps() and the bridge marker
// both miss — and, through confirmTwaFromReferrer, latched off for good so no
// later load has to catch a live signal at all.  Only OUR package matches, so a
// third-party Custom Tab launch can't false-flag (shared/twa-marker.js).
if (typeof document !== 'undefined') confirmTwaFromReferrer(document.referrer);

// The inline <head> script (index.html) may have already captured the
// MessagePort before this deferred module executed.  Adopt it now and
// replay any messages that arrived in the gap.
if (window._earlyTwaPort) {
  _twaPort = window._earlyTwaPort;
  confirmTwaSession();
  _twaPort.onmessage = (portEvent) => {
    handleTwaPortMessage(portEvent.data);
  };
  const queued = window._earlyTwaQueue || [];
  window._earlyTwaQueue = null;          // stop buffering
  window._earlyTwaPort = null;           // consumed
  queued.forEach((msg) => handleTwaPortMessage(msg));
  dbgUpdateChannel();
  dbgPortSrc('early');
  postConfigToNative('proactive/early');
}

// ---------------------------------------------------------------------------
// Native "waiting article" surface (ADR #2710) — transport-neutral
//
// A share captured while signed out is parked as a durable native record (the
// single saver) and named on the auth card so the user knows it's safe; the SPA
// NEVER queues it (resumeParkedSaves completes it natively after sign-in). The
// native wrapper pushes the newest waiting share's DISPLAY-ONLY label — or null
// when none — over WKWebView's callAsyncJavaScript. The handler is deliberately
// transport-neutral, but iOS/macOS are the only callers: Android's signed-out
// bounce carries the blob itself as `?text=…&pending=native` (#2758), so
// handleShareTarget names it and returns without queueing.
// It replaces the old macOS reload-the-webview transport (`?url=&pending=native`),
// whose reload on activation clobbered an in-flight federated sign-in — the
// sign-in return IS an activation. A push can't: nothing navigates.
//
// What arrives is a label, not a verdict (#3067): a share parked before sign-in
// hasn't been interpreted yet — only the backend decides whether a blob is a
// link or a document (ADR #1806) — so the native side sends its throwaway guess
// (SharedItemUrl.shareDisplayLabel: the link it carries, else its opening line).
//
// That label is ALREADY a display target, so it is rendered verbatim and the
// only thing decided here is the copy. Re-reading it through shareDisplayTarget
// would run URL_PATTERN over a whole URL, and that pattern stops at a `,` or an
// unbalanced paren by design — so it picks links out of prose but truncates one
// given whole: a shared `…/maps/@40.7128,-74.0060,15z` would render as
// `…/maps/@40.7128`. A bare URL, which is all older wrappers ever pushed,
// reads as a link and keeps the article copy exactly as it always did.
//
// Race-free: the wrapper pushes in response to the boot setConfig the SPA sends
// just below (so this handler is already defined by the time native calls back),
// plus on every later activation. Shown only while signed out — a signed-in user
// has no auth card — and only a header WE set is cleared, so a web share's own
// pending header is never wiped.
// ---------------------------------------------------------------------------
let _waitingArticleHeaderShown = false;
// A label that is nothing but a link — the whole string, not a link found
// inside one. Deliberately not URL_PATTERN: that one's job is to find links in
// prose, which is the opposite of the question here.
const BARE_LINK = /^https?:\/\/\S+$/;
function applyWaitingArticle(label) {
  if (label && !isLoggedIn()) {
    setAuthHeader(BARE_LINK.test(label)
      ? pendingUrlHeader(label)
      : { text: PENDING_SHARE_HEADER_TEXT, detail: label });
    _waitingArticleHeaderShown = true;
  } else if (_waitingArticleHeaderShown) {
    // Only clear a header this surface set — a null push (nothing parked, or the
    // record de-parked on sign-in) must not wipe a web pending submission's header.
    setAuthHeader(null);
    _waitingArticleHeaderShown = false;
  }
}
window.podstowSetWaitingArticle = applyWaitingArticle;

// iOS WKWebView (#1942): the wrapper's podstowSetConfig handler is registered before
// the page loads (no port handshake), so sync once at boot — auth.js pushes
// on every later auth change. This self-heals native storage left stale by an
// app update or an auth change the previous page load never delivered.
if (pushConfigToWebkit()) {
  dbgTx('setConfig/webkit (boot)');
  // Redacted summary of the pushed bundle — the decisive clue for an iOS
  // share that bounces to the handoff (see redactConfigSummary).
  dbgLog(`cfg: ${dbgConfigSummary()}`);
}

window.addEventListener('message', (event) => {
  // Log ALL window messages (before any filtering) so the dev debug
  // panel can show what Chrome and extensions are posting.
  dbgWmsg(event);

  // Chrome Custom Tabs port-delivery arrives with event.source === null
  // and event.origin === "android-app://<verified-web-host>" (the host
  // is the one passed to requestPostMessageChannel on the native side,
  // confirmed via the bridge debug panel on dev).  Both checks together
  // reject every other port-bearing message (cross-origin iframes,
  // extensions, SWs) so mobile web doesn't false-flag as TWA.  Match
  // the scheme with startsWith so a stray port or future format change
  // doesn't silently break the bridge again.
  if (event.ports && event.ports.length > 0
      && event.source === null
      && typeof event.origin === 'string'
      && event.origin.startsWith('android-app://')) {
    _twaPort = event.ports[0];
    confirmTwaSession();
    // A port arriving after the banner first rendered confirms the TWA late —
    // re-evaluate so the "get the app" nudge is pulled (mirrors EXT_PRESENT_EVENT).
    showInstallBanner();
    _twaPort.onmessage = (portEvent) => {
      handleTwaPortMessage(portEvent.data);
    };
    dbgUpdateChannel();
    // Refresh the "Active" line — ports arriving after DOMContentLoaded
    // would otherwise leave it stuck at the initial "none" value.
    dbgDetectBridge();
    dbgPortSrc('listener', event.source);
    postConfigToNative('proactive/listener');
    return;
  }

  // Fallback: if Chrome delivers postMessage data directly on the window
  // event (no ports), handle it here so the bridge still works.  Gated on
  // the same CCT signature as the port branch above (source === null +
  // verified android-app:// origin) — without it, ANY web opener could
  // post {type:'navigate'} and force same-origin navigation, including
  // /?url=… which auto-queues ingestion for a logged-in user.
  if (event.data
      && event.source === null
      && typeof event.origin === 'string'
      && event.origin.startsWith('android-app://')) {
    confirmTwaSession();
    // Portless CCT handshake — same late-confirmation re-evaluation as above.
    showInstallBanner();
    handleTwaPortMessage(event.data);
  }
});

// Page-lifecycle breadcrumbs for the debug panel. The TWA bridge is observed
// to die across background-return and in-tab navigation (settings→home), and
// the panel log is the only on-device view of what this document actually
// received. `port=` records whether THIS document holds a MessagePort at that
// moment: a return-to-foreground that logs port=n and is never followed by a
// `port:` adoption line means the native side re-requested a channel (or
// didn't) and Chrome delivered nothing — the discriminating signal for the
// bridge-recovery investigation.
window.addEventListener('pageshow', (e) => {
  // A bfcache restore re-enters with neither DOMContentLoaded nor an auth
  // change, so re-run the visibility gate here — a /settings toggle flip made
  // before pressing Back must take effect on the restored page.
  syncBridgeDebugPanel();
  dbgLog(`pageshow: persisted=${e.persisted ? 'y' : 'n'} port=${_twaPort ? 'y' : 'n'}`);
});
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    dbgLog(`vis: ${document.visibilityState} port=${_twaPort ? 'y' : 'n'}`);
  });
}

// Replay any window-level messages (no ports) that the inline <head>
// script buffered before this module loaded.  Chrome Custom Tabs can
// deliver requestConfig as a plain window message, so these must not
// be silently dropped.
const earlyMsgs = window._earlyTwaMsgQueue || [];
window._earlyTwaMsgQueue = null;          // stop buffering
// These were CCT-gated by the inline <head> script, so their presence
// confirms the TWA bridge — mark the tab before replaying them.
if (earlyMsgs.length > 0) confirmTwaSession();
earlyMsgs.forEach((data) => handleTwaPortMessage(data));

function handleTwaPortMessage(raw) {
  let data;
  try {
    data = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch { return; }

  if (data && data.type === 'requestConfig') {
    dbgRx(data.type);
    postConfigToNative('reactive');
  }

  // Deep-link navigation from the Android TWA.  The same-URL check
  // breaks the App Links loop (see MainActivity class KDoc).
  if (data && data.type === 'navigate' && data.url) {
    dbgRx(data.type);
    try {
      const target = new URL(data.url, location.origin);
      if (target.origin === location.origin && target.href !== location.href) {
        window.location.href = target.href;
      }
    } catch { /* malformed URL — ignore */ }
  }

  // A share save landed natively while this tab is open (#5236): refetch now
  // rather than on the next poll, through the listener the extension's and
  // Apple's nudges use.
  if (data && data.type === 'queueUpdated') {
    dbgRx(data.type);
    window.dispatchEvent(new CustomEvent(QUEUE_UPDATED_EVENT));
  }
}

// Android TWA transport only — the port is page-scoped and pull-based
// (MainActivity sends requestConfig on resume). The iOS webkit transport is
// page-global and SPA-pushed instead: see pushConfigToWebkit in
// shared/auth.js, which fires on every auth change; the boot-time call below
// the early-port block covers initial sync.
function postConfigToNative(reason) {
  if (!_twaPort) return;

  // Always send setConfig — the native side clears before writing, so
  // empty values on logout effectively clear stored credentials. The TWA
  // wire format is a JSON string (unlike webkit's object delivery); apiUrl
  // carries the full ingest URL Android's ApiHelper POSTs to verbatim —
  // see buildNativeConfigPayload for the per-transport contract.
  _twaPort.postMessage(JSON.stringify(
    buildNativeConfigPayload(`${window.location.origin}/api/ingest`),
  ));
  dbgTx(`setConfig (${reason || '?'})`);
}

// ---------------------------------------------------------------------------
// LocalStorage / sessionStorage keys
// ---------------------------------------------------------------------------
// Cleared on sign-in transitions so a returning user doesn't briefly see the
// previous account's feed URL on /settings.  settings.js owns the
// read/write of this key once the page is open.
const LS_FEED_ID = 'podstow_feed_id';
const SS_PENDING_URL = 'podstow_pending_url';
// A client-install signal captured off the URL fragment — the SESSION half of a
// two-part latch (the durable half is LS_INSTALL_HOWTO_OWED). It marks "this
// session began as the install session", so the value-first funnel knows to hold
// the how-to back behind the new-user "Try a sample" nudge until the first real
// save. Like SS_PENDING_URL it survives the sign-in round-trip (including the
// OAuth redirect, same tab/origin). Consumed alongside the durable half the
// moment the how-to is shown.
const SS_INSTALL_SIGNAL = 'podstow_install_signal';
// The DURABLE half of the install-how-to latch (localStorage, via safeStorage).
// captureInstallSignal writes it beside SS_INSTALL_SIGNAL and it outlives the
// session, so an install session that ends before the user's first save still
// leaves the how-to "owed": the next visit — which carries no fresh install
// fragment, so SS_INSTALL_SIGNAL is gone — reveals it then (alongside the
// still-pending sample nudge). Consumed with the session half on show, so the
// how-to appears exactly once across sessions.
const LS_INSTALL_HOWTO_OWED = 'podstow_install_howto_owed';
// Like SS_PENDING_URL, but for a pending TEXT submission (#1293): a JSON
// {title, text} stash that survives the OAuth round-trip when a logged-out
// user shares or pastes prose, and is resumed after sign-in.
const SS_PENDING_TEXT = 'podstow_pending_text';
// A raw human share awaiting sign-in (ADR #1806): the blob as the user handed
// it over, not a client-side verdict about what it is. The backend interprets
// it when it is finally submitted, so a stash that outlives a heuristic change
// is classified by the rule in force at SAVE time, not at share time.
const SS_PENDING_SHARE = 'podstow_pending_share';
// Set to 'window' when the SPA was opened from the browser extension's
// handoff (URL ?source=extension — the client id, ADR #1627).  dismiss()
// reads this to call window.close() so the chromeless popup window closes
// and the user is returned to the tab they shared from.  Survives the OAuth
// round-trip in sessionStorage like SS_PENDING_URL.
const SS_CLOSE_STRATEGY = 'podstow_close_strategy';

// ---------------------------------------------------------------------------
// DOM references (populated after DOMContentLoaded)
// ---------------------------------------------------------------------------
let $signinCard, $settingsLink,
    $takeover, $takeoverUrl, $takeoverStatus, $takeoverStatusText,
    $takeoverErrorText, $takeoverQuotaText,
    $pasteCard, $pasteInput, $pasteSubmit, $pasteInputError,
    $pasteErrorText, $pasteBlockedText,
    $saveSample, $saveSampleBtn,
    $installHowto, $installHowtoDone;

// Identify this surface + version on every API request (ADR #1627). The SPA
// is the canonical onboarding surface and is never gated; declaring it
// explicitly keeps the contract clear even though `web` is the shared
// default. The version is the fleet build version
// (MAJOR.MINOR.<commit-count>+<short-sha>), injected into config.js at deploy
// as window.PODSTOW_CLIENT_VERSION (see docs/versioning.md); absent in local
// dev, where we report `dev`.
configureClient('web', window.PODSTOW_CLIENT_VERSION || 'dev');

// Configure auth-ui callbacks at module level (before DOMContentLoaded) so
// tests that import this module get showToast wired up immediately.
configureAuthUI({ showToast });

// One no-op touch listener for the whole document (#3338). iOS withholds
// :active from a control unless a touch listener sits on it or above it, so
// without this the pressed states the sheets carry would light on Android and
// stay dead on iPhone. Every page that mounts the shared player registers its
// own — the landing demo and the episode page do it at their mount, this one
// covers the app bar's buttons too. Registered at module level rather than on
// DOMContentLoaded so it covers the first tap on a slow first paint, and
// passive so it cannot delay a scroll — it does nothing, and a non-passive
// listener would make the browser wait to find that out on every touch.
document.addEventListener('touchstart', () => {}, { passive: true });

// ---------------------------------------------------------------------------
// Update login/logout UI based on current auth state.
// Called on boot and via onAuthChange.
// ---------------------------------------------------------------------------
async function updateAuthUI() {
  const loggedIn = isLoggedIn();
  const takeoverActive = $takeover && !$takeover.hidden;

  // Sync the pre-paint `ps-authed` class with real auth state.  This is the
  // single source of truth for lean-view visibility — CSS rules on .ps-authed
  // / .ps-takeover gate #paste-card, #episodes-card and #install-howto.
  document.documentElement.classList.toggle('ps-authed', loggedIn);

  // signin-card and settings-link don't have CSS visibility rules of their
  // own, so we toggle their `hidden` attribute directly.
  if ($signinCard) $signinCard.hidden = loggedIn || takeoverActive;
  if ($settingsLink) $settingsLink.hidden = !loggedIn;

  if (loggedIn) {
    const signinForm = document.getElementById('signin-form');
    const signinError = document.getElementById('signin-error');
    if (signinForm) signinForm.reset();
    if (signinError) signinError.hidden = true;
    postConfigToNative('login');
    handleNextRedirect();
    // The token the outbox flush needs has just appeared (boot logged-in, or
    // an in-SPA sign-in after a lapsed session) — commit any positions a
    // previous session left pending. Self-guards on the outbox being empty.
    queueHost.signedIn();
    // The resolver decides what (if anything) to offer here — including the
    // desktop extension, which has no deferred prompt to gate on.
    showInstallBanner();
  } else {
    // Tell the native side to clear stored credentials so the share
    // receiver does not attempt API calls with stale tokens.
    postConfigToNative('logout');
    hideInstallBanner();
    // Stop in-page playback and drop the previous account's queue, so its
    // audio and titles can't outlive the logout on a shared device (#1991).
    queueHost.signedOut();
    // The now-playing shade (#2137) is part of the authed view — lower it on
    // logout, for the same shared-device reason.
    lowerShade();
    // The next user to sign in must re-earn the install-banner audience via
    // their own /me, and can't inherit this account's first-save state
    // (#2093 / #1680).
    resetPerAccountState();
  }
}

// ---------------------------------------------------------------------------
// Deep-link follow-through.  The /settings page bounces logged-out users to
// /?next=/settings; once they sign in (via form or OAuth), send them
// where they were going.  Only allow same-origin paths starting with a
// single "/" to defend against open-redirect abuse.
// ---------------------------------------------------------------------------
function handleNextRedirect() {
  if (!isLoggedIn()) return;
  const params = new URLSearchParams(location.search);
  const fromUrl = params.get('next');
  const fromStorage = sessionStorage.getItem(SS_NEXT_REDIRECT);
  const next = fromUrl || fromStorage;
  if (!next) return;
  sessionStorage.removeItem(SS_NEXT_REDIRECT);
  // Same-origin absolute paths only.  Require a leading "/" (rejects
  // "https://…", "javascript:…", bare relative paths) AND resolve against our
  // own origin so anything that escapes it is dropped: protocol-relative
  // ("//host"), backslash ("/\\host") and embedded-control-char forms all
  // normalise to a foreign origin that location.replace would otherwise honour
  // (open-redirect). The URL parse is the authoritative check; the prefix test
  // keeps the intent ("absolute path") explicit.
  if (!next.startsWith('/')) return;
  let resolved;
  try {
    resolved = new URL(next, location.origin);
  } catch {
    return;
  }
  if (resolved.origin !== location.origin) return;
  location.replace(next);
}

// ---------------------------------------------------------------------------
// Submit an article URL to the Ingestion API
//
// The takeover and paste card both call this with `silent: true` and
// manage their own UI feedback (per the agreed toast rule: never the only
// feedback channel for the user's primary intent).  Direct callers without
// a UI surface — e.g. tests or background restoration paths — pass
// `silent: false` to get the legacy error-toast fallback.
// ---------------------------------------------------------------------------
async function queueArticle(url, { silent = false } = {}) {
  // getToken() is the single authority: it handles expiry and transparent
  // refresh. Do not read localStorage directly — that would bypass refresh.
  const token = await getToken();
  if (!token) {
    if (silent) throw new Error('Not authenticated');
    showToast('Sign in to save articles.', 'error');
    return;
  }

  if (!url) {
    if (!silent) showToast('Enter an article URL.', 'error');
    return;
  }

  try {
    await sendUrl(url, { token });
  } catch (err) {
    console.error(err);
    if (!silent) showToast(err.message ?? 'Failed to save article.', 'error');
    throw err;
  }
  // A successful save stamps this client in the server `clients` map, so the
  // user is now established (#2093) — latch it locally so the install-banner
  // audience goes live in-session, not only on the next page-load /me snapshot.
  _established = true;
  // The new submission appears in the list immediately (#1989). Fire and
  // forget — the save already succeeded, and the next refetch self-heals.
  queueHost.refetch().catch(() => {});
}

// Forward a raw human share and let the backend interpret it (ADR #1806).
// The counterpart of queueArticle/queueText for the case where nobody yet
// knows which of the two it is: the paste box and the Web Share Target hand
// over exactly what the user gave us, and the response names the verdict.
async function queueShare(share, { silent = false } = {}) {
  const token = await getToken();
  if (!token) {
    if (silent) throw new Error('Not authenticated');
    showToast('Sign in to save this.', 'error');
    return;
  }
  try {
    await sendShare(
      {
        ...(share.text !== undefined ? { text: share.text } : {}),
        ...(share.url !== undefined ? { url: share.url } : {}),
      },
      { token },
    );
  } catch (err) {
    console.error(err);
    if (!silent) showToast(err.message ?? 'Failed to save.', 'error');
    throw err;
  }
  _established = true; // mirror queueArticle: a save makes the user established (#2093)
  queueHost.refetch().catch(() => {});  // surface the new row right away (#1989)
}

// Text counterpart of queueArticle (#1293): same token handling, posts the
// body via sendText. Callers with their own UI (the paste card, the takeover)
// pass silent:true and render their own feedback.
async function queueText({ title, text }, { silent = false } = {}) {
  const token = await getToken();
  if (!token) {
    if (silent) throw new Error('Not authenticated');
    showToast('Sign in to save text.', 'error');
    return;
  }
  try {
    await sendText({ title, text }, { token });
  } catch (err) {
    console.error(err);
    if (!silent) showToast(err.message ?? 'Failed to save text.', 'error');
    throw err;
  }
  _established = true; // mirror queueArticle: a save makes the user established (#2093)
  // Mirror queueArticle: surface the new row right away (#1989).
  queueHost.refetch().catch(() => {});
}

// ---------------------------------------------------------------------------
// Shared save-error handling — the SPA `web` save surfaces (share-target
// takeover, paste card) all map an /ingest failure the same way:
//
//   429 quota_exceeded →  quota      — hit the plan limit (the composed wall
//                                       copy + upgrade CTA)
//   429 rate_limited   →  throttled  — transient burst cap; retry copy, NO CTA (#2462)
//   401                →  authExpired — credentials rejected mid-flow
//   else               →  error      — a generic, retryable save failure
//
// The two 429s split on the server's machine `code` (ADR #864), never on the
// message prose: an abuse throttle clears in seconds, so it renders like a
// transient error, not the standing plan-limit upsell. An untagged 429 (a
// legacy quota response) still falls to quota — the safe default.
//
// 403 ("User not provisioned") never reaches `web`: a missing user row
// self-heals via /me (src/client-identity.ts), so it falls to the generic
// error branch.
//
// classifyWebSaveError owns the taxonomy; each surface renders the kind its own
// way (the takeover stashes + re-auths on 401, the inline paste card just shows
// copy). saveErrorMessage / saveErrorTextForKind own the copy.
// ---------------------------------------------------------------------------
const SESSION_EXPIRED_MESSAGE = 'Your session expired. Sign in again.';

// Generic "couldn't save" copy. Noun-aware (the takeover names "this article" /
// "this text"; the paste card stays generic with no noun). The HTTP code is
// appended for 5xx (a support reference); a generic 4xx carries its own server
// message; a statusless network error stays clean.
function saveErrorMessage(err, { noun } = {}) {
  const status = err?.status;
  const fallback = `Couldn't save ${noun ? `this ${noun}` : 'this'}.`;
  if (status === undefined) return fallback;
  if (status >= 500) return `${fallback} ${httpRef(status)}`;
  return err?.message ?? fallback;
}

// The sentence the wall shows (#3328), composed from the server's refusal
// facts — with the fixed PLAN_LIMIT_MESSAGE as the guard for a refusal that
// carried no facts (an older lambda mid-deploy, an untagged 429), which is
// also what ApiError falls back to in `.message`. One helper so the takeover,
// the paste card, and the dropped-save toast can't each pick a different line.
//
// Composed here rather than read off `err.message` because ONE fact in that
// sentence is ours to supply: whether this surface can actually sell a pack
// (#3416). The server's `unblock` list is tier-derived and client-blind by
// design (#3328), and ApiError — shared with the extension and /billing, which
// are only ever a plain browser — cannot ask. The SPA is the one surface a
// store shell hosts, so it answers with the same predicate /settings renders
// its pack card from (shell.js): offer a pack only where there is one to buy.
function quotaWallCopy(err) {
  return composeQuotaMessage(err?.quota, { packDoor: hasPackDoor() })
    || err?.message
    || PLAN_LIMIT_MESSAGE;
}

// The facts of the refusal the takeover is currently showing, so its sentence
// can be re-composed if the pack door answers late. Null whenever the takeover
// is not on the wall.
//
// The wall composes at the moment of refusal, and the door is not always
// answered by then: a share-save POSTs before app.js reads /me (see the boot
// sequence), and a pack carrying a `tiers` list is unprovable until the tier
// is known. So the first sentence can be honestly conservative — "Upgrade in
// Settings" on a surface that does have a pack door — with nothing to revisit
// it, because a takeover is one screen and the user acts on it once.
//
// One direction only, which is the constraint's
// (docs/constraints/billing-store-cards.md): a wall that GAINS a pack once the
// store answers is a beat of honest copy; a wall that lost one would have been
// offering a door that never existed. `hasPackDoor()` is one-way within an
// account, so re-composing can only add the clause.
let _takeoverQuotaErr = null;

// The wall's sentence, from the refusal it is showing. One writer, so the
// first render and the re-render after a late door cannot word it differently.
function renderTakeoverQuotaCopy() {
  if ($takeoverQuotaText) $takeoverQuotaText.textContent = quotaWallCopy(_takeoverQuotaErr);
}

onPackDoorOpen(() => {
  // Null unless the wall is the screen on show — `setTakeoverState` drops it on
  // every other state — so a door proven after the takeover moved on to a
  // pending, successful or failed save re-words nothing.
  if (!_takeoverQuotaErr) return;
  renderTakeoverQuotaCopy();
});

// Classify an /ingest failure into one of the shared outcome kinds. The two
// 429s split on the machine `code` (ADR #864): a transient burst-cap throttle
// (`rate_limited`) is not the standing plan-quota refusal (#2462). Any other
// 429 — including an untagged legacy quota response — stays 'quota'.
function classifyWebSaveError(err) {
  const status = err?.status;
  if (status === 429) return err?.code === 'rate_limited' ? 'throttled' : 'quota';
  if (status === 401) return 'authExpired';
  return 'error';
}

// Display string for an already-classified failure on the inline paste card,
// which renders 401 as a message rather than re-authing. Callers that also need
// the kind (the paste card's upgrade-link toggle) classify once and pass it in.
function saveErrorTextForKind(kind, err, { noun } = {}) {
  switch (kind) {
    case 'quota':       return quotaWallCopy(err);
    case 'throttled':   return THROTTLED_MESSAGE;
    case 'authExpired': return SESSION_EXPIRED_MESSAGE;
    default:            return saveErrorMessage(err, { noun });
  }
}

// ---------------------------------------------------------------------------
// Share-target takeover
//
// The takeover is the user's only feedback channel when they invoke the SPA
// via Web Share Target, the browser extension, or a sessionStorage-pending
// URL surviving an OAuth round-trip.  Four mutually-exclusive states are
// driven by setTakeoverState():
//
//   pending  →  spinner, waiting for /ingest to return
//   success  →  tick + "Done" button → dismiss()
//   quota    →  429 — "You've hit your plan limit" + manage-subscription link
//   error    →  5xx/network — Retry / Done
//
// Forced sign-in (logged-out cold-share, or 401 mid-flow) is not a takeover
// state: it surfaces the regular auth card with a contextual header set via
// pendingUrlHeader() + setAuthHeader().
// ---------------------------------------------------------------------------
const TAKEOVER_STATES = ['pending', 'success', 'quota', 'error'];
// State → the .takeover-view it puts on screen.  Pending and success share the
// queue view (the spinner→tick swap happens inside it), so three views cover
// four states.  The state class does the LOOK; this map puts the same fact in
// the DOM (docs/constraints/accessible-names.md), where it holds before the
// stylesheet lands and where a rule with no layout engine can read it — the
// three views carry a `Done` button each.
const TAKEOVER_VIEW = {
  pending: 'queue',
  success: 'queue',
  quota:   'quota',
  error:   'error',
};
// State → id of the primary action button to focus when the state opens.
// Stays in sync with the markup in index.html.  Skipped for 'pending'
// because there is no actionable button while the spinner shows.
const TAKEOVER_FOCUS_BTN = {
  success: 'takeover-done-btn',
  quota:   'takeover-quota-link',
  error:   'takeover-retry-btn',
};

// Auth-card header content used when a sign-in is required to resume queueing
// a stashed URL.  Structured args (text + detail) so setAuthHeader can use
// textContent for both — no innerHTML, no escaping concerns.
const PENDING_URL_HEADER_TEXT = 'Sign in to save this article.';
function pendingUrlHeader(url) {
  return { text: PENDING_URL_HEADER_TEXT, detail: url };
}

const PENDING_TEXT_HEADER_TEXT = 'Sign in to save this text.';
function pendingTextHeader(submission) {
  return { text: PENDING_TEXT_HEADER_TEXT, detail: submission.title };
}

// A raw share hasn't been interpreted yet — only the backend decides whether
// it is a link or a document (ADR #1806) — so name it with a throwaway,
// DISPLAY-ONLY guess: the link it carries, else its opening line. Explicitly
// non-authoritative: it never touches what gets submitted, and the server's
// verdict may differ (a long note ending in a reference link shows the link
// here and is still narrated). Deliberately not a copy of the real rule —
// scripts/arch-fitness.mjs fails the build if the authoritative names come
// back into a client.
const PENDING_SHARE_HEADER_TEXT = 'Sign in to save this.';
function shareDisplayTarget(share) {
  const blob = (share.url || '').trim() || (share.text || '').trim();
  const links = extractUrls(blob);
  if (links.length > 0) return { kind: 'link', value: links[links.length - 1] };
  const firstLine = blob.split(/\r?\n/).map((l) => l.trim()).find(Boolean) || '';
  return { kind: 'text', value: firstLine.slice(0, 100) };
}
function pendingShareHeader(submission) {
  // The sign-in card shows the link in full, as it did for a ?url= share.
  return { text: PENDING_SHARE_HEADER_TEXT, detail: shareDisplayTarget(submission).value };
}

function pendingSubmissionHeader(submission) {
  if (submission.kind === 'share') return pendingShareHeader(submission);
  return submission.kind === 'text'
    ? pendingTextHeader(submission)
    : pendingUrlHeader(submission.url);
}

// Pending-submission stash, surviving the OAuth round-trip (#1293). Everything
// written now is a raw share in SS_PENDING_SHARE (JSON). The pre-#1806 keys —
// SS_PENDING_URL (a bare string) and SS_PENDING_TEXT (JSON) — are still read on
// resume, for the sessions that straddled the deploy, but are never written.
function stashPending(submission) {
  // A pending submission is singular — the latest share/paste supersedes any
  // earlier one. Clear every key first so two stashes can never coexist (an
  // older one wins on resume, which would silently drop the newer share).
  clearPending();
  if (submission.kind === 'share') {
    sessionStorage.setItem(
      SS_PENDING_SHARE,
      // JSON.stringify drops undefined members, so an absent field stays absent
      // across the round-trip rather than resuming as an empty string.
      JSON.stringify({ text: submission.text, url: submission.url }),
    );
  } else if (submission.kind === 'text') {
    sessionStorage.setItem(
      SS_PENDING_TEXT,
      JSON.stringify({ title: submission.title, text: submission.text }),
    );
  } else {
    sessionStorage.setItem(SS_PENDING_URL, submission.url);
  }
}
// Returns the stashed text submission ({kind:'text', title, text}) or null.
function readPendingText() {
  const raw = sessionStorage.getItem(SS_PENDING_TEXT);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.title === 'string' && typeof parsed.text === 'string') {
      return { kind: 'text', title: parsed.title, text: parsed.text };
    }
  } catch { /* ignore a corrupt stash */ }
  return null;
}
// Returns the stashed raw share ({kind:'share', text, url}) or null.
function readPendingShare() {
  const raw = sessionStorage.getItem(SS_PENDING_SHARE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && (typeof parsed.text === 'string' || typeof parsed.url === 'string')) {
      return { kind: 'share', text: parsed.text, url: parsed.url };
    }
  } catch { /* ignore a corrupt stash */ }
  return null;
}

// The pending submission to resume after an OAuth round-trip, or null if
// nothing is stashed. New stashes are always raw shares; the URL and text keys
// are read for the sessions that straddled the deploy which stopped writing
// them, and can be dropped once those have expired.
function readPendingSubmission() {
  const share = readPendingShare();
  if (share) return share;
  const url = sessionStorage.getItem(SS_PENDING_URL);
  if (url) return { kind: 'url', url };
  return readPendingText();
}
function clearPending() {
  sessionStorage.removeItem(SS_PENDING_URL);
  sessionStorage.removeItem(SS_PENDING_TEXT);
  sessionStorage.removeItem(SS_PENDING_SHARE);
}

// First-run welcome (ADR #1627 §7): a fresh, logged-out arrival — a
// just-installed extension or native app opening the SPA, or a first-time web
// visitor — starts on the sign-up panel rather than the default sign-in form.
// That panel's welcome surface (the value line + how-it-works strip) carries
// the welcome. selectAuthWelcome owns the decision (skipping returning users and
// pending-save boots, which keep the sign-in default). Runs after
// handleShareTarget so a pending-save boot is already detectable.
function maybeShowFirstRunWelcome() {
  const welcome = selectAuthWelcome({
    loggedIn: isLoggedIn(),
    returningUser: isReturningUser(),
    // A pending text submission (#1293) is a pending save too — keep the
    // sign-in default rather than the first-run welcome panel. Reuse
    // readPendingSubmission so the URL-then-text precedence lives in one place.
    pendingUrl: readPendingSubmission() ? 'pending' : null,
  });
  if (!welcome) return;
  showAuthPanel(welcome.panel);
}

// The submission the takeover is currently acting on: {kind:'url', url} or
// {kind:'text', title, text} (#1293). Drives the retry button.
let _takeoverSubmission = null;
let _takeoverPrevFocus = null;

function setTakeoverState(state) {
  if (!$takeover) return;
  // Leaving the wall drops the refusal it was showing, so `_takeoverQuotaErr`
  // means "the refusal on screen now" and a later door opening cannot re-word
  // a save that has moved on.
  if (state !== 'quota') _takeoverQuotaErr = null;
  for (const s of TAKEOVER_STATES) {
    $takeover.classList.toggle(`takeover-state-${s}`, s === state);
  }
  // Say in the DOM which views this state does not show, rather than leaving
  // the stylesheet to be the only record of it.
  const shown = `takeover-view-${TAKEOVER_VIEW[state]}`;
  for (const view of $takeover.querySelectorAll('.takeover-view')) {
    view.hidden = !view.classList.contains(shown);
  }
  // Pending and success share one save-status block (the shared spinner→tick
  // primitive); flip it in place so there's no second screen / layout shift.
  if (state === 'pending' || state === 'success') {
    renderSaveStatus($takeoverStatus, $takeoverStatusText, state === 'success');
  }
  // Pending and success share one view; the Done button is visible in both
  // but only clickable once /ingest has acknowledged the URL.
  const doneBtn = document.getElementById('takeover-done-btn');
  if (doneBtn) doneBtn.disabled = state !== 'success';
  // The header's home + settings affordances are inert until the save settles:
  // there's nothing to navigate to mid-save, and a stray tap mustn't abandon the
  // in-flight save (the cog also greys via the state class in CSS).  Carry the
  // disabled state to pointer-free / AT users via aria-disabled + tabindex; the
  // click handlers re-check aria-disabled as a belt-and-braces guard.
  const headerLive = state !== 'pending';
  for (const id of ['takeover-home', 'takeover-settings']) {
    const el = document.getElementById(id);
    if (!el) continue;
    el.setAttribute('aria-disabled', headerLive ? 'false' : 'true');
    if (headerLive) el.removeAttribute('tabindex');
    else el.setAttribute('tabindex', '-1');
  }
  // Move focus to the primary action so keyboard / screen-reader users
  // land on the next thing to do.  Wrapped in requestAnimationFrame so the
  // browser has applied the state class (and unhidden the matching view)
  // before .focus() runs — otherwise the call no-ops on display:none.
  const focusId = TAKEOVER_FOCUS_BTN[state];
  if (focusId && typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      const el = document.getElementById(focusId);
      el?.focus?.();
    });
  }
}

// The takeover shows the source's hostname rather than the full URL — the
// path/query carries no meaning for the user, and the hostname fits on one
// line.  Falls back to the raw input if URL parsing fails (extractUrls can
// pass through the original text when no http(s) URL was found).
function urlDisplayLabel(url) {
  try { return new URL(url).hostname; } catch { return url; }
}

// Remember the element that triggered the takeover so we can restore focus on
// dismiss.  Only capture once per opening so re-entry into setTakeoverState()
// doesn't clobber it.
function rememberTakeoverFocus() {
  if ($takeover?.hidden !== false) {
    _takeoverPrevFocus = document.activeElement ?? null;
  }
}

// Open the takeover overlay for either submission kind (#1293). A URL is named
// by hostname; a text submission by its derived title.
function applyTakeover(submission) {
  rememberTakeoverFocus();
  _takeoverSubmission = submission;
  document.documentElement.classList.add('ps-takeover');
  if ($takeover) $takeover.hidden = false;
  if ($takeoverUrl) {
    if (submission.kind === 'share') {
      // Advisory only — the backend's verdict lands with the response. A link
      // shows as its hostname, matching how a ?url= share always read.
      const target = shareDisplayTarget(submission);
      $takeoverUrl.textContent = target.kind === 'link'
        ? urlDisplayLabel(target.value)
        : (target.value || 'your text');
    } else {
      $takeoverUrl.textContent = submission.kind === 'text'
        ? (submission.title || 'your text')
        : urlDisplayLabel(submission.url);
    }
  }
  if ($signinCard) $signinCard.hidden = true;
}

// URL-only wrapper, exported for tests.
function showTakeover(url) {
  applyTakeover({ kind: 'url', url });
}

function hideTakeover() {
  if ($takeover) $takeover.hidden = true;
  document.documentElement.classList.remove('ps-takeover');
  _takeoverSubmission = null;
  // Dismissal is the other way off the wall, and the only one that does not
  // pass through `setTakeoverState`. The refusal goes with the screen, or a
  // door opening afterwards re-words a save the user has finished with —
  // onto a surface nobody is looking at, ready for the next takeover to
  // flash it.
  _takeoverQuotaErr = null;
  // The lean view (and its episode list) is the surface now — start the
  // poll loop a takeover boot skipped. Restart is harmless: a fresh fetch
  // plus re-arm, which the just-finished save wants anyway (#1990).
  queueHost.start();
  // Re-evaluate the visibility of the underlying surfaces.
  updateAuthUI().catch(console.error);
  // Restore focus to the element that opened the takeover.  Guarded
  // against detached / focus-refusing elements via the optional call.
  const prev = _takeoverPrevFocus;
  _takeoverPrevFocus = null;
  prev?.focus?.();
}

// URL-only wrapper, exported for tests.
function queueTakeoverUrl(url) {
  return queueTakeover({ kind: 'url', url });
}

// Drive the takeover state machine for either submission kind (#1293). Queues
// via queuePaste (URL → scrape, text → narrate), and on a mid-flow 401 stashes
// the right kind for post-sign-in resume.
async function queueTakeover(submission) {
  setTakeoverState('pending');
  try {
    await queuePaste(submission);
  } catch (err) {
    // A raw share hasn't been interpreted yet, so name it neutrally.
    const noun = submission.kind === 'share'
      ? 'item'
      : (submission.kind === 'text' ? 'text' : 'article');
    switch (classifyWebSaveError(err)) {
      case 'quota':
        // The takeover's button already routes to Settings, which is exactly
        // where the composed copy sends the user (#3328).
        //
        // Held, not just rendered: the pack door may still be unproven, and the
        // subscriber above re-words this same refusal if it opens while the
        // wall is still the screen (#3817).
        _takeoverQuotaErr = err;
        renderTakeoverQuotaCopy();
        setTakeoverState('quota');
        break;
      case 'authExpired':
        // Server rejected the credentials mid-flow (revoked, scope change,
        // tampered token).  Drop the dead session locally so the auth card
        // unhides, stash the submission for post-sign-in resume, and surface
        // the auth card with a contextual header.
        stashPending(submission);
        setAuthHeader(pendingSubmissionHeader(submission));
        clearLocalSession();
        hideTakeover();
        break;
      default:
        if ($takeoverErrorText) $takeoverErrorText.textContent = saveErrorMessage(err, { noun });
        setTakeoverState('error');
    }
    return;
  }

  // /ingest succeeded. A saved article just shows the Saved tick + Done;
  // podcast-app subscription lives in Settings now, so there is no post-save
  // subscribe nag (ADR #2089 §4).
  setTakeoverState('success');
}

// ---------------------------------------------------------------------------
// Multi-strategy dismiss helper.
//
// Tries in order:
//   1. Android TWA — postMessage {type: "close"} over the verified channel,
//      so MainActivity finishes the wrapping activity.
//   2. iOS / macOS WKWebView — webkit.messageHandlers.close.postMessage so the
//      hosting view controller can background the app, then return the webview
//      to home (the app only backgrounds, not quits, so its one persistent
//      webview must not be left on the stale Saved takeover for the reopen).
//   3. Browser-extension popup window — dispatch a DOM event the
//      content-script bridge forwards to the background, which calls
//      chrome.windows.remove.  (window.close() is silently refused for
//      chrome.windows.create windows because the page script didn't open
//      them, so the bridge is required.)
//   4. PWA / window.opener — window.close() works for popups and for an
//      installed PWA in standalone display mode.
//   5. Plain browser tab — navigate to /, clearing the share-target params
//      so the user lands on the lean main view.
// ---------------------------------------------------------------------------
function dismiss() {
  // The user is leaving — stop the episodes poll loop (#1990). On Android TWA /
  // iOS the native shell only *backgrounds* the webview, so the JS context (and
  // any armed timer) survives the close; the visibility gate is the off-switch
  // we can't rely on here, so stop it explicitly. The plain-tab fallback
  // navigates to '/', which boots a fresh loop.
  queueHost.stop();
  // The user is off the wall, so the refusal it was showing is no longer "the
  // refusal on screen now" — the meaning `_takeoverQuotaErr` carries for the
  // `onPackDoorOpen` subscriber. Dropped here rather than left to the branches:
  // the TWA arm posts `close` and returns, and the shell only BACKGROUNDS the
  // webview, so this document survives and a door opening afterwards would
  // re-word a save the user has finished with — onto a surface nobody is
  // looking at, ready for the next takeover to flash it.
  _takeoverQuotaErr = null;

  if (_twaPort) {
    try {
      _twaPort.postMessage(JSON.stringify({ type: 'close' }));
      return;
    } catch (err) {
      console.warn('TWA close failed:', err);
    }
  }

  const ios = window.webkit?.messageHandlers?.close;
  if (ios) {
    try {
      ios.postMessage({ type: 'close' });
      // The Apple wrapper only *backgrounds* the app here — iOS suspends,
      // macOS hides (ContentView's `close` handler) — it doesn't quit, so the
      // single persistent WKWebView and its rendered page survive. Left on the
      // Saved takeover, reopening the app (dock / app icon) resurfaces that
      // stale success card instead of home. Return the webview to home so it
      // reloads off-screen and a later reopen lands there — the same in-SPA hop
      // native sign-out uses to converge with web (#2607 follow-up).
      location.href = '/';
      return;
    } catch (err) {
      console.warn('iOS close failed:', err);
    }
  }

  if (sessionStorage.getItem(SS_CLOSE_STRATEGY) === 'window') {
    // Ask the extension's content-script bridge to close the window.
    // window.close() doesn't work here (chrome.windows.create windows
    // aren't script-closable from the hosted page), so we dispatch a DOM
    // event that sync-token.js forwards as a runtime message; the
    // background removes this tab — closing Chrome's chromeless popup with
    // it, or just the full-tab handoff on Safari and mobile.  The timeout
    // is longer than the PWA path because of the page → content script
    // → background → API round-trip, and covers bridge-less opens too
    // (Safari before the user grants the extension site access).
    window.dispatchEvent(new Event(CLOSE_WINDOW_EVENT));
    setTimeout(() => {
      if (!window.closed) location.href = '/';
    }, 300);
    return;
  }

  const standalone = window.matchMedia?.('(display-mode: standalone)').matches
    || navigator.standalone === true;
  if (window.opener || standalone) {
    try {
      window.close();
    } catch (err) {
      console.warn('window.close failed:', err);
    }
    // window.close() is a silent no-op outside popups and installed PWAs.
    // The setTimeout lets the close finish on real installs and falls
    // through to / on browsers that refused.
    setTimeout(() => {
      if (!window.closed) location.href = '/';
    }, 50);
    return;
  }

  location.href = '/';
}

// Dismiss when the user taps the empty backdrop outside the card — the area
// below (and beside) the top-aligned card, which on a phone is the easy
// one-handed reach.  Scoped to the success state only: the save's already done,
// so an accidental tap costs nothing, and it mirrors tap-the-backdrop-to-close.
// Deliberately NOT wired for the other states — pending would abandon the
// in-flight save, and in quota / error Done is the *secondary* action (Manage
// subscription / Retry lead), so mapping the big reachable target to dismiss
// would be a fat-finger trap.  Pointer-only: the real Done button stays the
// single labelled, focusable control, so the tab order / SR experience is
// unchanged.  e.target === $takeover means a genuine backdrop tap — a click
// bubbled up from the card or a button carries that inner element as target.
function handleTakeoverBackdropClick(e) {
  if (e.target !== $takeover) return;
  if (!$takeover?.classList.contains('takeover-state-success')) return;
  dismiss();
}

// Route a takeover destination (home, settings, subscription — e.g. the quota
// takeover's "Manage subscription" <a href>) through the extension bridge.
// Inside the extension handoff (SS_CLOSE_STRATEGY 'window' + a content-script
// presence marker) we dispatch a bare DOM event the sync-token.js bridge
// forwards to the background as OPEN_APP_PATH.  The event NAME carries the
// destination (no CustomEvent detail, which Firefox sanitises across the world
// boundary), and the background resolves it to a fixed path — the page can
// never inject a raw URL.  What the background does is per-surface: Chrome's
// handoff is a chromeless 520px popup that would trap the destination, so it
// opens a real tab in the main browser window and closes the popup; Safari's
// handoff is a full tab, so it navigates that tab in place.
//
// Returns true when it intercepted (the click handler then preventDefaults);
// returns false — letting the <a href> navigate in place — in a plain browser
// tab and on bridge-less opens (Safari before the user grants the extension
// site access, where the handoff is a full tab anyway).
//
// No in-place fallback timer: the getExtensionMarker guard means the bridge IS
// present, so it will act — and on Chrome, navigating the cramped popup is the
// very thing we're escaping.  If the bridge ever no-ops, the takeover's "Done"
// button stays as a manual out (unlike dismiss()'s close-window path, which
// has no marker guard and so must fall back for bridge-less tabs).
function handleTakeoverNav(eventName) {
  if (sessionStorage.getItem(SS_CLOSE_STRATEGY) !== 'window') return false;
  if (!getExtensionMarker(document)) return false;
  window.dispatchEvent(new Event(eventName));
  return true;
}

// The quota "Manage subscription" link is one such destination.
function handleManageSubscription() {
  return handleTakeoverNav(OPEN_APP_EVENTS.subscription);
}

// ---------------------------------------------------------------------------
// Handle Android Web Share Target and browser-extension handoff (GET ?url=…).
//
// On any trigger — query param OR session-stashed pending URL — open the
// takeover overlay.  For authenticated users we POST /ingest straight away.
// For logged-out users we stash the URL and show the sign-in CTA; the boot
// sequence resumes the takeover after the OAuth round-trip.
// ---------------------------------------------------------------------------
function handleShareTarget() {
  const params = new URLSearchParams(location.search);
  const sharedUrlParam = params.get('url') || '';
  const sharedTextParam = params.get('text') || '';
  // Resolve the submission this open is about:
  //   1. either share-target query field — forwarded raw, both of them, and
  //      the BACKEND decides which link wins and whether the blob is a link or
  //      a document (ADR #1806). The two fields used to answer that question
  //      differently — `?url=` took the first link, `?text=` the last (#3054)
  //      — because each was interpreted by separate client-side code;
  //   2. a stash resumed after the OAuth round-trip.
  let submission = null;
  let fromQuery = false;
  if (sharedUrlParam || sharedTextParam.trim()) {
    submission = {
      kind: 'share',
      ...(sharedTextParam ? { text: sharedTextParam } : {}),
      ...(sharedUrlParam ? { url: sharedUrlParam } : {}),
    };
    fromQuery = true;
  } else {
    // No query trigger — resume an OAuth-round-trip stash, if any.
    submission = readPendingSubmission();
  }

  if (!submission) {
    // No trigger — make sure the pre-paint takeover class doesn't linger
    // (the inline detector errs on the side of showing the overlay).
    document.documentElement.classList.remove('ps-takeover');
    return;
  }

  // A genuine share/handoff is in flight, so arm the extension popup-window
  // close strategy.  This must stay *below* the !submission guard: an
  // extension *onboarding* open also carries source=extension but no
  // submission, and lives in a regular browser tab — arming 'window' there
  // would make dismiss() ask the background to chrome.windows.remove the
  // user's whole window, not a dedicated popup.
  if (params.get('source') === 'extension') {
    sessionStorage.setItem(SS_CLOSE_STRATEGY, 'window');
  }

  // Clear the URL bar immediately to avoid re-triggering on reload.
  if (fromQuery) history.replaceState(null, '', location.pathname);

  // Display-only handoff (ADR #2710 2026-07-18 amendment): a natively-captured
  // share reloaded with pending=native is already parked as a durable native
  // record — the single saver, completed by the native re-submit on the sign-in
  // bridge push. Name it on the sign-in screen so the user knows it's safe, but
  // NEVER stash or queue it (that would be a second, un-deduped ingest — two
  // idempotency keys, no server dedup). Gated on the marker: a markerless ?url=
  // (PWA share target, browser extension) falls through and still saves below.
  if (params.get('pending') === 'native') {
    setAuthHeader(pendingSubmissionHeader(submission));
    document.documentElement.classList.remove('ps-takeover');
    return;
  }

  if (!isLoggedIn()) {
    // Stash the submission and let the auth card prompt the user to sign in.
    // The header explains why; onSignedIn re-opens the takeover and queues.
    stashPending(submission);
    setAuthHeader(pendingSubmissionHeader(submission));
    // Pre-paint may have set ps-takeover before we knew the user was
    // logged-out — drop it so the auth card unhides.
    document.documentElement.classList.remove('ps-takeover');
    return;
  }

  // Authenticated — consume the stash (if any) and queue.
  clearPending();
  applyTakeover(submission);
  queueTakeover(submission).catch(console.error);
}

// ---------------------------------------------------------------------------
// Client-install how-to (clients/shared/client-install.js) — value-first funnel
//
// A freshly-installed satellite opens — or, for the extension, pings an
// already-open tab of — the home document with the `#podstow-installed=<client>`
// fragment. The how-to is a *signed-in* surface, and it is SEQUENCED behind the
// new-user "Try a sample" nudge (below) so a brand-new installer never faces two
// onboarding CTAs at once. captureInstallSignal() records the client in a
// two-part latch — a sessionStorage marker (this is the install session) and a
// durable localStorage flag (the how-to is owed) — and tidies the URL.
// syncInstallHowto() then reconciles that latch with first-use state:
//   - install session + brand-new user (no real save yet) → DEFER: the sample
//     nudge owns the first screen; the how-to is revealed on the first real save
//     (the queue host's onEpisodes re-runs this), so the two never compete.
//   - a user who already has saves (a re-install — no funnel to run), or a
//     RETURN visit whose session marker is gone but the durable flag persists →
//     show it now (the nudge, if still pending, coexists per its own gate).
// Both latch halves survive the sign-in round-trip (incl. the OAuth redirect,
// which drops URL fragments) the same way SS_PENDING_URL does, and are consumed
// together on show so the how-to appears exactly once across sessions. Keyed
// ONLY on the fragment — never `?source=`, the handoff marker (ADR #1627 §7).
// ---------------------------------------------------------------------------

// Whether the user has saved a real article yet — a queue row carrying `savedBy`
// (#2108). null until the first episodes fetch resolves; the queue host's onEpisodes owns it.
// syncInstallHowto reads it to decide defer-vs-reveal, so `null` (state unknown)
// reads as "not yet saved" and keeps the how-to deferred rather than flashing it.
let _hasSavedArticle = null;

// Test hook: drive the first-use state the funnel gates on, without standing up a
// full episodes fetch (exported as _setHasSavedArticle).
function setHasSavedArticleForTest(v) { _hasSavedArticle = v; }

// Capture the signal off the URL fragment into the two-part latch and tidy the
// URL. Called at boot and on hashchange (the extension's same-document ping of an
// already-open tab). Latching — rather than acting inline — is what lets a
// logged-out installer still get the how-to once they sign in, and lets the
// funnel defer it. Recognised clients we render nothing for (e.g. mcp) are still
// captured so the fragment never lingers; syncInstallHowto consumes them silently.
function captureInstallSignal() {
  const client = parseInstallSignal(location.hash);
  if (!client) return; // not an install signal — leave any unrelated hash alone
  sessionStorage.setItem(SS_INSTALL_SIGNAL, client); // this-session marker
  safeStorage.setItem(LS_INSTALL_HOWTO_OWED, client); // durable "owed" flag
  history.replaceState(null, '', location.pathname + location.search);
  // An install signal proves the client is present now. Suppress its install
  // prompt for the session and clear the banner if it's the one being offered —
  // this same-document ping doesn't inject the content script, so the
  // data-podstow-ext marker (the resolver's "installed here" signal) won't be
  // set on THIS tab until a reload (#1680).
  sessionStorage.setItem(SS_INSTALL_SUPPRESS, client);
  if (_activeInstall && _activeInstall.client === client) hideInstallBanner();
}

// Consume both halves of the latch so the how-to shows exactly once across
// sessions (a later refresh, a fresh session, or the ✕ all leave it gone).
function consumeInstallSignal() {
  sessionStorage.removeItem(SS_INSTALL_SIGNAL);
  safeStorage.removeItem(LS_INSTALL_HOWTO_OWED);
}

// Read the durable owed latch, enforcing per-account scope on a shared device
// (the #2093 discipline the progress outbox's `owner` applies): capture
// happens logged out, so the value starts unowned (a bare client id) and the
// FIRST signed-in reconcile adopts it by stamping the account's sub. A
// different account signing in later drops the latch instead of inheriting
// the previous account's how-to. No usable sub (no id token) degrades to the
// unscoped read — same behavior as before the scoping existed.
function owedInstallHowto() {
  const raw = safeStorage.getItem(LS_INSTALL_HOWTO_OWED);
  if (!raw) return null;
  const sub = getUserSub();
  if (raw.startsWith('{')) {
    let owned = null;
    try { owned = JSON.parse(raw); } catch { /* malformed — drop below */ }
    const foreign = owned && owned.sub && sub && owned.sub !== sub;
    if (!owned || typeof owned.client !== 'string' || foreign) {
      // Drop BOTH halves: captureInstallSignal writes them together, so a
      // foreign/malformed durable half means the session marker is the same
      // install's — leaving it would let `sessionSignal || durable` reveal
      // the previous account's how-to on a same-tab account switch.
      consumeInstallSignal();
      return null;
    }
    return owned.client;
  }
  // Unowned (captured logged out, or written pre-scoping) — adopt it.
  if (sub) safeStorage.setItem(LS_INSTALL_HOWTO_OWED, JSON.stringify({ client: raw, sub }));
  return raw;
}

// Chrome/Edge list each extension with an inline pin in the extensions
// dropdown the user must pin; Firefox adds a newly-installed extension to the
// toolbar by default, so its copy skips the pinning step. The extension
// how-to carries both copies (data-browser); this picks the one for the running
// browser via the shared classifier. Everything that isn't Firefox gets the
// Chromium flow — Edge/Brave/Opera are Chromium under the hood, and Safari
// can't run this ext.
function extensionBrowser(userAgent) {
  return getDesktopBrowser(userAgent) === 'firefox' ? 'firefox' : 'chromium';
}

// Reconcile the client-install how-to with the latch + the user's first-use
// state (value-first onboarding). Called once auth settles, on the extension's
// hashchange ping, and after every episodes refresh. Guarded on isLoggedIn so a
// signal captured while logged out waits — onSignedIn and the post-auth boot
// step re-run this the moment sign-in completes — and on $installHowto so an
// early call (refs not yet wired) doesn't consume the latch before it can show.
function syncInstallHowto() {
  if (!isLoggedIn() || !$installHowto) return;
  // Read (and adopt/scope) the durable half FIRST: a foreign-account drop in
  // there consumes the session marker too, and reading sessionStorage before
  // it would hand the dropped marker's value straight back via the local.
  const durable = owedInstallHowto();
  // The session marker takes priority, but the durable flag alone (its session
  // ended before the first save) drives the return-visit reveal.
  const sessionSignal = sessionStorage.getItem(SS_INSTALL_SIGNAL);
  const client = sessionSignal || durable;
  if (!client) return;
  // Reveal the per-client block whose data-for matches (a `~=` token match, so
  // one block can serve several clients). `client` is a fixed id from
  // parseInstallSignal, so it's safe to interpolate into the selector. A
  // recognised client with no block (e.g. mcp) has no funnel to run: consume it
  // now — both halves, so it never lingers — and show nothing.
  const block = $installHowto.querySelector(`[data-for~="${client}"]`);
  if (!block) { consumeInstallSignal(); return; }
  // Value-first: on the install session, hold the how-to back until the user has
  // made their first real save so the "Try a sample" nudge owns the first screen.
  // `_hasSavedArticle !== true` covers both a brand-new user (false) and the
  // pre-episodes unknown state (null). Not the install session (a return visit,
  // durable flag only) shows regardless — decision C's graceful fallback.
  if (sessionSignal && _hasSavedArticle !== true) return;
  consumeInstallSignal();
  block.hidden = false;
  // The extension block carries per-browser copy; reveal only this browser's.
  if (client === 'extension') {
    const variant = block.querySelector(`[data-browser="${extensionBrowser(navigator.userAgent)}"]`);
    if (variant) variant.hidden = false;
  }
  $installHowto.hidden = false;
}

function dismissInstallHowto() {
  if ($installHowto) $installHowto.hidden = true;
}

// Bind at module load (like the other window listeners) so the extension's
// in-place ping of an already-open tab — a same-document fragment change, which
// fires `hashchange`, not a reload — is caught: capture it, then show or defer
// per the funnel if the user is already signed in.
window.addEventListener('hashchange', () => { captureInstallSignal(); syncInstallHowto(); });

// ---------------------------------------------------------------------------
// Paste-and-queue card
//
// One feedback model — button-local (#2019), no toast, no full-screen takeover
// (the user is already on the SPA). The input stays put, the Save button shows
// a spinner while ingest is in flight, and the new queue row (#1989) is the
// confirmation; the card never swaps to a progress/success view. A genuinely
// empty queue (no welcome episode — a historic account) works the same way: the
// row appears in the list once the save resolves.
// ---------------------------------------------------------------------------

// Toggle the Save button's in-flight spinner (button-local feedback, #2019):
// disable the button and overlay a spinner on the 'p' mark while ingest runs.
function setPasteSubmitBusy(busy) {
  if (!$pasteSubmit) return;
  $pasteSubmit.classList.toggle('is-busy', busy);
  $pasteSubmit.disabled = busy;
  $pasteSubmit.setAttribute('aria-busy', busy ? 'true' : 'false');
}

// Dismiss a lingering inline save error (#2019) — the restored value + Save is
// itself the retry, so editing or resubmitting clears the error in place. A
// no-op outside the error state.
function clearStalePasteError() {
  if ($pasteCard?.classList.contains('paste-state-error')) setPasteState('input');
}

// Side effects when the paste box value changes — whether the user types or
// "Try a sample" fills it: drop the inline validation error and any lingering
// save error (#2019). The single owner, so every value-change path stays in
// step (no hand-rolled subset that drifts when the input contract grows).
function dismissPasteInputErrors() {
  if ($pasteInputError && !$pasteInputError.hidden) $pasteInputError.hidden = true;
  clearStalePasteError();
}

// State → the .paste-view blocks it puts on screen.  An ingest failure keeps
// the pill (the input IS the retry) and adds the message beneath it, so `error`
// shows two.  As with the takeover, the state class does the look and this map
// puts it in the DOM too (docs/constraints/accessible-names.md), so the blocked
// panel's Manage-subscription link and the Save button it replaces are never
// both on record as reachable.
const PASTE_VIEWS = {
  input:   ['input'],
  error:   ['input', 'error'],
  blocked: ['blocked'],
};

// The card rests in one of three states: `input` (the slim bar), `error` (the
// bar with a message beneath — the input itself is the retry), or `blocked`
// (the out-of-quota panel). Saves are button-local, so there's no progress or
// success state.
function setPasteState(state) {
  if (!$pasteCard) return;
  for (const s of ['input', 'error', 'blocked']) {
    $pasteCard.classList.toggle(`paste-state-${s}`, s === state);
  }
  const shown = PASTE_VIEWS[state] ?? [];
  for (const view of $pasteCard.querySelectorAll('.paste-view')) {
    view.hidden = !shown.some((v) => view.classList.contains(`paste-view-${v}`));
  }
  // The nudge sits outside the views but only belongs to the resting input
  // one; CSS used to drop it, which left its button reachable on a screen
  // that does not show it.
  syncSampleNudge();
  if (state === 'input' && $pasteInputError) $pasteInputError.hidden = true;
}

// The "New here? Try a sample" nudge shows on the resting input view, and only
// until the user has saved something real.  `_hasSavedArticle` is null until
// the first episodes read answers, and an unknown answer keeps it hidden — a
// nudge that flashes at a returning user is worse than one that arrives a beat
// late.
function syncSampleNudge() {
  if (!$saveSample) return;
  const offInputView = $pasteCard?.classList.contains('paste-state-input') === false;
  $saveSample.hidden = _hasSavedArticle !== false || offInputView;
}

// Queue a classified paste/share submission — a URL is scraped, text is
// narrated directly (#1293). Both surfaces (card, takeover) manage their own
// UI, so this stays silent.
async function queuePaste(submission) {
  if (submission.kind === 'share') {
    await queueShare(submission, { silent: true });
  } else if (submission.kind === 'text') {
    await queueText(submission, { silent: true });
  } else {
    await queueArticle(submission.url, { silent: true });
  }
}

// Button-local save (#2019). Clears the input optimistically so the next paste
// is immediate, spins the Save button while ingest runs, and leans on the new
// queue row (#1989) as the confirmation. On failure the entered value is
// restored and the inline error surfaces. No phantom row can linger: the queue
// only refetches (queueHost.refetch) once queuePaste resolves, so an error never
// inserts a row in the first place.
async function submitPaste() {
  if (!$pasteInput) return;
  // The save gesture is happening — retire the new-user button nudge.
  clearSampleNudge();
  const rawValue = $pasteInput.value;
  const text = rawValue.trim();
  if (!text) {
    if ($pasteInputError) {
      $pasteInputError.textContent = 'Paste a link or some text.';
      $pasteInputError.hidden = false;
    }
    return;
  }
  // Forward what the user pasted, untouched. Whether it is a link to scrape or
  // prose to narrate is the backend's call (ADR #1806) — which is also what
  // lets that rule be retuned without shipping every client again.
  const submission = { kind: 'share', text };

  clearStalePasteError();
  $pasteInput.value = '';
  setPasteSubmitBusy(true);
  try {
    await queuePaste(submission);
    refreshUsageAfterPasteSave();
  } catch (err) {
    $pasteInput.value = rawValue;  // restore so the user can retry
    renderPasteError(err);
  } finally {
    setPasteSubmitBusy(false);
  }
}

function renderPasteError(err) {
  const kind = classifyWebSaveError(err);
  // A quota refusal isn't a transient, retryable slip — it's a standing state.
  // It only reaches here as a race (the save fired before /me had locked the
  // card). Surface the *dropped attempt* as a toast ("not saved") and lock the
  // card to the blocked panel — the single Manage-subscription CTA — rather
  // than the inline error message, whose retry is guaranteed to fail again.
  if (kind === 'quota') {
    showToast(saveNotSavedToast(err), 'error');
    // The refusal's `allowance` is the one fresh figure on this path — the race
    // exists precisely because /me hasn't landed — so hand it to the panel.
    setPasteBlocked(_lastUsageData, err?.quota);
    return;
  }
  // Generic noun — the paste card saves a link OR raw text (#1293) — so no noun.
  if ($pasteErrorText) $pasteErrorText.textContent = saveErrorTextForKind(kind, err);
  setPasteState('error');
}

// ---------------------------------------------------------------------------
// New-user sample (ADR #2089 §4)
//
// A quiet "Try a sample" line inside the Save panel, shown until the user has
// saved a real article (the queue host's onEpisodes gates it on the queue holding only the
// provisioned welcome episode). It doesn't save for the user — it drops the one
// fixed, curated demo article (title + URL) into the paste box so they press the
// Save button themselves, which teaches the save gesture. The demo is cache-warm
// (deduped) and quota-exempt (config/gratis-articles.json, #1648), and the
// "<title> - <url>" form resolves to a URL save server-side (ADR #1806), so the
// normal Save flow scrapes the essay. The URL is the shared demo-article module
// so it can't drift from the gratis allowlist.
// ---------------------------------------------------------------------------
function useSample() {
  if (!$pasteInput) return;
  // A short caption + the URL: the backend reads that as a URL save (scrape),
  // and the visible title tells the user what they're about to save.
  $pasteInput.value = `${DEMO_ARTICLE.title} - ${DEMO_ARTICLE.url}`;
  // Fan out through the same handler a keystroke would, so any stale inline /
  // save error clears.
  dismissPasteInputErrors();
  // Show the caption from the *left* — the demo title, "what am I about to
  // save?" — not the scrolled-to-end tail of the URL (#2089 §4). Put the
  // caret at the very start and scroll the box back; leaving the input
  // unfocused keeps the mobile keyboard down, since the next action is the
  // Save button, not typing.
  $pasteInput.setSelectionRange?.(0, 0);
  $pasteInput.scrollLeft = 0;
  // Pull the eye — and keyboard focus — to the Save button: that's the step
  // that teaches the save gesture. The highlight rides on the button (.is-nudged,
  // style.css) rather than the filled box, and clears on the first edit or save.
  $pasteSubmit?.classList.add('is-nudged');
  $pasteSubmit?.focus?.();
}

// Drop the new-user Save-button nudge once it's served its purpose — the user
// has started editing, or a save is under way. Single owner so the highlight
// never lingers past that first interaction.
function clearSampleNudge() {
  $pasteSubmit?.classList.remove('is-nudged');
}

// ---------------------------------------------------------------------------
// Out-of-quota Save card (blocked panel)
//
// When the allowance is genuinely spent (deriveUsage().blocked) there's no
// point offering an input the save is guaranteed to refuse: the Save card swaps
// its input + button for a blocked panel — the message plus the single
// Manage-subscription CTA. It is the home screen's only conversion surface:
// nothing nudges before the wall (#3443).
//
// Two paths reach the panel: a /me refresh (renderSaveQuota, the steady-state
// signal) and a 429 race where a save fired before /me had locked the card
// (renderPasteError → setPasteBlocked, plus a "not saved" toast for the dropped
// attempt). `_lastUsageData` is the most recent /me so the race path can render
// the same allowance/reset copy.
// ---------------------------------------------------------------------------

// Toast copy for a save dropped on a quota refusal — names the dropped action
// ("not saved") in front of the wall's own sentence, so the toast says what was
// lost and the composed copy says when it lifts and where to fix it.
const saveNotSavedToast = (err) => `Not saved — ${quotaWallCopy(err)}`;

let _lastUsageData = null;

// A paste-card save spends usage, so refresh /me to keep the out-of-quota
// panel current while the user stays on the page — without it `_lastUsageData`
// only refreshes on the next visibility-change/page-load /me, so the save that
// spends the last article would leave the input offered when the next save
// would only be refused (renderSaveQuota then locks the blocked panel from the
// fresh figures). Fire and forget, like the episode refetch; other save
// surfaces self-heal on their next /me. Only the paste card carries the quota
// UI that needs the immediate signal.
function refreshUsageAfterPasteSave() {
  fetchAccountInfo().catch(() => {});
}

// Blocked-panel copy from a /me-shaped object, or — on the 429 race, where /me
// hasn't landed yet — from the refusal's own facts (#3328), which carry the
// same allowance and reset instant the server just gated on. Only a refusal
// with neither falls back to the period-agnostic line.
function blockedCopy(data, facts) {
  const u = deriveUsage(data);
  if (u.limit > 0) {
    const reset = formatPeriodReset(data?.usage, data?.limits);
    return `You've saved all ${u.limit} articles.` + (reset ? ` ${reset}.` : '');
  }
  if (facts?.allowance > 0) {
    const reset = formatResetInstant(Date.parse(String(facts.resetsAt ?? '')));
    return `You've saved all ${facts.allowance} articles.` + (reset ? ` ${reset}.` : '');
  }
  return "You've saved all your articles for now.";
}

// Lock the Save card to the blocked panel, refreshing the copy from the latest
// usage figures (the passed data, or the last /me we cached).
function setPasteBlocked(data, facts) {
  if (data) _lastUsageData = data;
  if ($pasteBlockedText) $pasteBlockedText.textContent = blockedCopy(_lastUsageData, facts);
  setPasteState('blocked');
}

// Reconcile the Save card with the latest /me. Only retags the card's *resting*
// state (input ↔ blocked) — never stomps a transient error the user is still
// reading, which resolves to the right resting state on its own.
function renderSaveQuota(data) {
  _lastUsageData = data;
  const resting = $pasteCard?.classList.contains('paste-state-input')
    || $pasteCard?.classList.contains('paste-state-blocked');
  if (!resting) return;
  if (deriveUsage(data).blocked) setPasteBlocked(data);
  else if ($pasteCard?.classList.contains('paste-state-blocked')) setPasteState('input');
}

// ---------------------------------------------------------------------------
// Main-page episode list (#1989, ADR #1986) — every submission as a row
// below the save box, served from GET /api/episodes (never feed XML; the
// arch fitness function holds clients to that). Freshness is the
// visibility-gated poller (#1990, ADR #1987): interval polling while the
// tab is visible — tight while a row is processing, relaxed otherwise,
// fully paused while hidden — plus an immediate refetch on visibility or
// focus regain and after each save.
//
// The list IS the playback queue (#1991): the rows are rendered by the shared
// queue player (ADR #1961), which owns play/pause, seek, speed, auto-advance,
// traversal, and Media Session. The queue host (queue-host.js, #4850) owns
// everything between the signed-in account and that player — the fetch, the
// poll loop, the outbox overlay and projection, the durable progress writes,
// the page-hide flush, the boot resume cue, the track-end refetch and the
// flight recorder. This page mounts it, tells it of sign-in, sign-out and
// saves, and hangs its own UI (the shade, the header toggle, the save-card
// funnel) off its hooks.
// ---------------------------------------------------------------------------
let $episodesCard = null;
let $episodesList = null;

const queueHost = createQueueHost({
  api: httpQueueApi(apiRequest),
  auth: { isLoggedIn, getToken, peekAccessToken, getUserSub },
  emitDiagnostics: (payload) => emitEvent({ event_type: 'playback-diagnostics', props: payload }),
  playerOptions: {
    // The main page adopts the shared player's "page" presentation (#2007):
    // bare chrome, a docked mini-player and multi-line rows. That treatment now
    // lives in shared/queue-player.css behind the queue-player--page class, so
    // the SPA opts in here rather than re-architecting the component via
    // .episodes-card descendant overrides.
    variant: 'page',
    // The welcome row's lead tile carries the podstow mark (#3352). The path is
    // the SPA's to know, not the shared component's (ADR #1961): this is the
    // symlink into clients/shared/mark.svg, so the mark stays the one set of
    // brand bytes brand:check guards (docs/constraints/brand-assets.md).
    markUrl: '/shared/mark.svg',
    // Toggle the now-playing detail shade (#2137) from the docked player's
    // expand control (a tap on its title row, or an upward flick), for whatever
    // row is focused. The live-drag twin — an upward drag that lifts the shade
    // under the finger — reports through the trio below.
    onExpandDetails: toggleShade,
    onExpandMove: dragRaiseShade,
    onExpandCommit: commitRaiseShade,
    onExpandCancel: cancelRaiseShade,
    // Failed rows disclose inline (#2136): the player owns the in-place
    // disclosure + report form, but stays dumb (ADR #1961) — the POST is
    // delegated here, and the shared report copy is threaded in (the player must
    // not import beyond its engine seam, arch-fitness §5).
    onReport: submitInlineReport,
    reportHelpText: REPORT_HELP_TEXT,
    reportNotesPlaceholder: REPORT_NOTES_PLACEHOLDER,
    // The admin audio trace watches the element and the engine's calls.
    onAudio: (audio) => audioTrace.attach(audio),
    onDiagnostic: (event) => audioTrace.note(event),
  },
  onPlayerMount: relocateListenedToggle,
  onPlayerEvent: handleQueuePlayerEvent,
  onEpisodes: (episodes) => {
    // The "new here? Try a sample" nudge shows until the user has saved a real
    // article — i.e. while the queue is empty OR holds only the provisioned
    // welcome episode (#2092). The server stamps `savedBy` (the saving client)
    // on every real save and leaves it off the server-seeded welcome row
    // (#2108), so the moment a row carrying a savedBy appears (any real save),
    // this hides. Keying off the authoritative server signal rather than
    // sniffing a magic 'welcome' jobId.
    _hasSavedArticle = episodes.some((e) => e?.savedBy);
    syncSampleNudge();
    // Value-first onboarding: on an install session the client-install how-to was
    // deferred so this nudge could own the first screen — the user's first real
    // save (which just hid the nudge above) is its cue to appear, so the two CTAs
    // never overlap. This also drives the return-visit reveal once episodes confirm
    // the first-use state. No-op when there's no install signal pending.
    syncInstallHowto();
    // A blocked Save card re-reads /me on each refresh (#5082). A page that stays
    // visible and focused gets no resume signal, so a pack bought on another
    // device, or the allowance reset passing, would otherwise leave it blocked.
    // An unblocked card has nothing a background read would correct. A processing
    // row puts the poll on its 5-second cadence (the save that spent the last
    // article leaves one), so the read waits for the minute-long idle poll.
    if ($pasteCard?.classList.contains('paste-state-blocked')
      && !episodes.some((e) => e?.status === 'processing')) refreshAccountInfo();
  },
  // A refresh can drop the focused row itself (a failed/duplicate row dismissed,
  // a reorder), in which case the player drops its browse cursor and focus falls
  // back to the playing row — so re-point the shade at the new focus before it
  // diverges from what Share/Report act on. refreshShadeFocus re-renders only when
  // the focus actually moved; syncShadeNav still runs for an added/dropped
  // neighbour that left the focus itself unchanged (#2138).
  onRendered: () => { if (_shade?.isOpen()) { refreshShadeFocus(); syncShadeNav(); } },
  // Reveal the Listen section (heading + queue player) for every authed account
  // once a working queue stands in it, and keep it up even when the queue holds
  // nothing. A provisioned account always carries the welcome episode (#2092),
  // so an empty ("MT") queue is a historic edge that no longer occurs — and an
  // empty Listen section there is harmless.
  onShown: () => { if ($episodesCard) $episodesCard.hidden = false; },
  // A share-target / extension takeover covers the lean view for its whole
  // lifetime — there is no queue underneath it.
  isCovered: () => Boolean($takeover && !$takeover.hidden),
});

// Relocate the player's show/hide-listened eye toggle (#2011) into the sticky
// app header, just left of the settings gear, rather than leaving it at the foot
// of the queue. The header never scrolls away, so revealing a long listened
// backlog and collapsing it again both stay one tap off — no scroll back to the
// top. The player owns the node (it keeps showing/hiding and re-labelling it via
// its retained reference); we only move it once, here at mount, and skin it as a
// .header-icon-btn so it matches the gear. Skipped when there's no header ref
// (a headless mount), leaving the toggle in the player root.
//
// It then lives OUTSIDE the player root, so the player's own teardown
// (root.textContent = '') can't reclaim it: the returned cleanup removes it, or
// it lingers wired to a dead player and every later mount adds another — a stack
// of dead eyes accumulating across logout/login cycles on a shared device.
function relocateListenedToggle(player) {
  const $listenedToggle = player.element.querySelector('.queue-player-listened-toggle');
  if (!$settingsLink?.parentNode || !$listenedToggle) return undefined;
  $listenedToggle.classList.add('header-icon-btn');
  $settingsLink.parentNode.insertBefore($listenedToggle, $settingsLink);
  return () => $listenedToggle.remove();
}

// The page's half of the player's events: the shade, the transcript warm-up and
// the activation signals. The host has already done its own bookkeeping and
// makes the durable write after this returns.
function handleQueuePlayerEvent(type, detail) {
  // The now-playing shade (#2137) follows focus: when the user plays a row or
  // the queue auto-advances, an open shade re-renders to the new article.
  if (type === 'play' || type === 'auto-advance' || type === 'replay') {
    refreshShadeFocus();
    // Warm the now-playing episode's OWN transcript up front, so raising the
    // shade later renders it already scrolled to the live sentence rather than
    // fetching on open and scrolling into place after the raise settles.
    preloadTranscriptForId(detail.id);
  }
  // Activation signals (#2913): this user's first listen and first
  // listen-through. Which player event means which lives in the shared helper,
  // so this surface and the standalone /episode page can't drift on it; both
  // are at-most-once per user per device inside it, so there is no state to
  // keep here and no need to filter what we pass.
  recordPlaybackActivation(type, { jobId: detail.id });
  // The queue played out (no next row). The docked player idles itself and the
  // played rows fold away, so an OPEN shade would otherwise be left holding the
  // last article — stale title, body, and a frozen read-along highlight — with a
  // transport that has already reset to 0:00. Lower it instead: an empty docked
  // player above an empty queue is the honest "you're fully caught up" signal.
  // Route through history.back so the raise's one history entry is unwound (the
  // popstate handler runs lowerShade), exactly as the sheet grabber and the back
  // gesture lower it.
  if (type === 'queue-end') {
    // Hold the shade up if a Report is open (#reading-shade-report-ux): a new user
    // working a single episode is often still typing when it ends, and lowering here
    // would drop their half-written report. Leave the form up — they submit or dismiss
    // it themselves, and lowering resumes once it's closed. (Not routed through the
    // popstate discard-guard: the queue ending is not a dismiss the reader reached
    // for, so popping "Discard your report?" unprompted would just confuse.) Skip
    // mid-live-raise (_raising): isOpen() is already true once beginRaise renders,
    // but the entry isn't pushed until the drag commits, so a back() then would pop
    // the wrong entry.
    if (_shade?.isOpen() && !_raising && !_shade.isReportOpen?.()) history.back();
    return;
  }
  // Read-along (#2235): the per-cue 'position' event drives the shade's
  // transcript highlight. UI-only — no durable write.
  if (type === 'position' && detail.id) _shade?.updatePosition(detail);
}

// ---------------------------------------------------------------------------
// Episode detail surface (shared wiring)
// ---------------------------------------------------------------------------
// The now-playing shade (#2137, below) is the in-app detail surface. The
// helpers here — the report POST, the share fallback, the transcript fetch and
// the per-row details builder — are shared by the shade and the failed-row
// inline disclosure (#2136). The standalone /episode page is a separate
// surface, reached only by inbound deep links.
// Submit a problem report for the open episode from inside the shade — the
// same POST report.html makes (shared via problem-report.js), kept in-page so
// the queue keeps playing rather than navigating to the standalone report
// form. Resolves on success (the shade clears the form and returns to details)
// and rejects on failure (the shade keeps the form open so the notes survive).
async function submitEpisodeReport(details, notes) {
  const token = await getToken();
  if (!token) {
    showToast('Sign in to submit a problem report.', 'error');
    throw new Error('not signed in');
  }
  try {
    const data = await submitProblemReport({ token, episode: details, notes, request: apiRequest });
    showToast(reportSubmittedMessage(data.credited));
  } catch (err) {
    showToast('Failed to submit report.', 'error');
    throw err;
  }
}

// Submit a problem report from a failed row's inline disclosure (#2136). The
// player hands back the failed row's id; we rebuild the report details from the
// row we already hold (the same builder the shade uses) and reuse the in-page
// report POST. episodeDetailsForRow backfills the jobId (keys the #1772
// re-credit) and articleUrl from the row, so a failed row with no details href
// still files a report carrying the source URL. Resolves/rejects so the
// disclosure can clear or keep its form, exactly like the shade's onReport. No
// navigation, no playback interruption.
async function submitInlineReport(id, notes) {
  const item = queueHost.item(id);
  if (!item) throw new Error('episode no longer in the list');
  // This surface only shows on a failed row, so the report is implicitly about a
  // generation failure — fold that in (with the row's error headline) since there
  // is no picker here to carry a category. The jobId still keys the DLT for the
  // fuller reason.
  await submitEpisodeReport(
    episodeDetailsForRow(item, item.href),
    composeFailedEpisodeNotes({ notes, error: item.error }),
  );
}

// Client-side transcript cache + next-episode preload.
//
// Transcript objects are immutable, content-addressed /text/raw/* CloudFront
// files (the URL carries the job id, so it changes when an episode is
// re-generated), so a fetched body is safe to keep and reuse for ANY later focus
// of the same episode — a re-focus, a timeline roam back to it, or an
// auto-advance / forward swipe onto a row we preloaded. We keep a small LRU of
// bodies keyed by URL and dedupe concurrent fetches of the same URL (an open
// racing its own preload), so the network sees each transcript at most once.
// The fetch, the cache and the cues-else-text fallback are the shared loader's
// (shared/transcript-fetch.js), which the public episode page reads through too.
const _transcripts = createTranscriptCache();

// Fetch the focused episode's transcript and deliver it to the shade, then warm
// the NEXT episode's transcript so a forward roam / auto-advance lands it
// instantly. setTranscript is keyed by jobId, so a slow response for a
// since-replaced episode is dropped by the shade, and a shade lowered mid-fetch
// ignores the delivery; a cache hit skips the network (delivery is still a
// microtask via .then, just with no fetch).
function loadEpisodeTranscript(surface, details) {
  // Read-along (#2235, ADR #2952): the cue artifact is cached like a transcript
  // (an immutable, content-addressed /audio/<hash>/ file). A cache hit delivers
  // SYNCHRONOUSLY so the panel renders and scrolls to the live sentence in the
  // same frame it's revealed — on open, and crucially on a carousel commit, where
  // an async microtask would land the scroll only AFTER the peek is dropped,
  // reading as a jump (#2274). A miss fetches; any miss/parse failure degrades to
  // the plain transcript, never an error (Journey 4) — which is also how a
  // pre-#2953 episode, whose artifact was never written, renders.
  const cues = cachedCues(details, _transcripts);
  if (cues) {
    deliverTranscriptCues(surface, details, cues);
  } else if (details.cuesUrl || details.textUrl) {
    loadTranscript(details, _transcripts)
      .then((content) => {
        if (content?.cues) deliverTranscriptCues(surface, details, content.cues);
        else if (content) surface.setTranscript(details.jobId, content.text);
      })
      .catch(() => surface.setTranscript(details.jobId, null));
  }
  preloadAdjacentTranscripts();
}

// Render the read-along cues into the surface and register the player's cue
// boundaries. setTranscriptCues is jobId-guarded by the panel (a stale fetch is
// dropped); guard the player's single cue-point slot the same way so a late fetch
// for a since-browsed-away episode can't clobber the focused one's registration
// and freeze its highlight (#2235).
function deliverTranscriptCues(surface, details, cues) {
  // Seed the read-along at the listener's resume offset so the shade opens
  // already highlighted + scrolled to the live sentence, not on the first word
  // until the first play-driven position tick (#2235).
  surface.setTranscriptCues(details.jobId, cues, resumePositionSec(details.jobId));
  queueHost.setCuePoints(details.jobId, cues.map((c) => c.startSec));
}

// The read-along position to open an episode's transcript on (#2235): the live
// offset while it is the loaded (playing / paused) track, else its server-fed
// resume offset from the queue table for the cold-launch case where the focused
// row is only primed (not yet started), so the highlight lands where the listener
// left off rather than at the top. null when there is no usable offset (a
// never-started row), under which the transcript renders unhighlighted.
function resumePositionSec(jobId) {
  const live = queueHost.currentProgress();
  if (live && live.id === jobId && Number.isFinite(live.positionSec)) return live.positionSec;
  const item = queueHost.item(jobId);
  return item && Number.isFinite(item.positionSec) ? item.positionSec : null;
}

// The destination episode's cached transcript content for the carousel peek
// (#2227, #2232) — a synchronous cache read, null when nothing is cached yet.
// Prefers the read-along cues parsed from the cached artifact, so the peek renders
// the SAME paragraph structure the live panel lands on and the swipe no longer
// flips from plain text to read-along (#2235); falls back to plain text for an
// episode with no cues (or a degenerate artifact), matching the live panel's own
// degradation.
function peekTranscriptContent(details) {
  const content = cachedTranscript(details, _transcripts);
  if (!content) return null;
  // Hand the peek the resume offset the live panel will seed from, so the preview
  // tracks in already at that sentence rather than at the top with a jump when it
  // lands (#2274). Covers the loaded (playing / paused) track and the cold-launch
  // primed resume row alike; any other neighbour has no offset and starts at the
  // top. Read at swipe time — a live track advancing a cue before the panel lands
  // re-anchors on landing, the same one-cue settle the old loaded-track peek had.
  const position = resumePositionSec(details.jobId);
  if (Number.isFinite(position)) content.positionSec = position;
  return content;
}

// Warm, in the background, the transcripts of BOTH the articles a swipe could land
// on next (the player's navigable neighbours either way) — so the carousel peek
// (#2227) shows real content as it tracks in, and the forward commit / auto-advance
// lands instantly, instead of a spinner. Prefer the read-along cue artifact (what
// both the peek and the landed live panel render, #2235); fall back to the plain
// transcript for an episode with no cues. Best-effort: a failed or absent preload is swallowed;
// the real open surfaces any error and retries. Forward (the common travel
// direction) is warmed first; back covers a roam into listened history.
function preloadAdjacentTranscripts() {
  for (const direction of ['forward', 'back']) {
    preloadTranscriptForId(queueHost.player()?.peek?.(direction));
  }
}

// Warm one episode's transcript (its read-along cue artifact, else its plain text) into
// the cache, best-effort. Shared by the neighbour preload (#2232) and the
// now-playing warm: a cached transcript renders — and scrolls to the live
// sentence — in the same frame the shade reveals, instead of being fetched on
// open and visibly scrolling into place once the raise/open settles.
function preloadTranscriptForId(id) {
  if (!id) return;
  const item = queueHost.item(id);
  if (!item?.href) return;
  const { textUrl, cuesUrl } = episodeDetailsForRow(item, item.href);
  const url = cuesUrl || textUrl;
  if (!url || _transcripts.has(url)) return;
  _transcripts.fetch(url).catch(() => {});
}

// Drop the cache + any in-flight fetches — on a fresh mount / account switch, so
// one user's queue can't serve another's (the URLs are public, but the hygiene is
// cheap), and as a test reset hook.
function _resetTranscriptCache() {
  _transcripts.clear();
}

// Test hook: whether a transcript URL is currently cached (e.g. to await a
// background preload landing).
function _transcriptCached(url) {
  return _transcripts.has(url);
}

// Build the shade/report details for a queue row. The table row is the source
// of truth: the canonical href is now just `/episode/<jobId>` (the capability),
// so it carries no self-describing params — every display field rides on the
// item instead (id, title, articleUrl, textUrl, timestamp, the favicon, the
// failure copy, and the detail-view metadata: intro, byline, publisher,
// narrator). A legacy `/episode?…` href is still parsed for its params, but the
// item's own values backfill anything it doesn't carry. One builder for the
// shade and inline-report paths so they can't drift.
function episodeDetailsForRow(item, href) {
  const details = episodeDetailsFromHref(href ?? '', {
    sourceIcon: item?.sourceIcon ?? '',
    ...(item?.sourceKind && { sourceKind: item.sourceKind }),
    ...(item?.error && { error: item.error }),
    ...(item?.episodeMeta ?? {}),
  });
  if (!details.jobId && item?.id) details.jobId = item.id;
  if (!details.articleUrl && item?.articleUrl) details.articleUrl = item.articleUrl;
  if (!details.title && item?.title) details.title = item.title;
  if (!details.timestamp && item?.timestamp) details.timestamp = item.timestamp;
  if (!details.textUrl && item?.textUrl) details.textUrl = item.textUrl;
  return details;
}

// Back/forward drive the shade (#2137). It is NOT URL-addressable — raising it
// pushes a single history entry carrying only a flag (no URL change). A forward
// entry carrying psShade re-raises it; any other entry (the back gesture lands
// on the entry below, with no psShade) lowers it. The shade is part of the
// authed view, so a logged-out traversal never resurrects it (inbound
// /episode/<jobId> deep links still resolve to the standalone page).
window.addEventListener('popstate', (event) => {
  if (event.state?.psShade && isLoggedIn()) { openShade(); return; }
  // Guard a half-typed Report against an accidental dismiss (#reading-shade-report-ux):
  // the shade's close vectors (the OS/browser back, the sheet grabber and the
  // wordmark) all funnel through history.back → here, so this one chokepoint covers
  // them. When the open Report carries typed notes, re-arm the shade's history entry so it stays
  // up and surface the inline discard confirm in place of the close — the reader keeps
  // editing or confirms the discard (which clears the notes and lets the next close
  // through). An empty report, or no report, lowers as before.
  if (_shade?.isReportDirty?.()) {
    history.pushState({ psShade: true }, '');
    _shade.promptDiscard();
    return;
  }
  lowerShade();
});

// Arrow keys roam the timeline while the shade is open (#2138) — the desktop
// non-touch affordance alongside the shade's on-screen prev/next. → steps
// forward through the queue, ← back into listened history. Ignored while a
// modifier is held (browser shortcuts stay intact) or while typing in the
// Report form, and a no-op when the shade is lowered.
window.addEventListener('keydown', (event) => {
  // _shadeFocusId is nulled the instant lowering begins, so this also bows out
  // during the close slide (when isOpen() is briefly still true) — a stray arrow
  // there must not resurrect the browse cursor endBrowse() just dropped.
  if (!_shade?.isOpen() || _shadeFocusId === null) return;
  if (event.metaKey || event.ctrlKey || event.altKey) return;
  if (event.target?.closest?.('textarea, input')) return;
  if (event.key === 'ArrowRight') { event.preventDefault?.(); navigateShade('forward'); }
  else if (event.key === 'ArrowLeft') { event.preventDefault?.(); navigateShade('back'); }
});

// ---------------------------------------------------------------------------
// Now-playing detail shade (#2137, PRD #2129)
// ---------------------------------------------------------------------------
// A detail shade that rises from the docked player showing the FOCUSED
// (now-playing / cued) article's details (intro, transcript, source, Share,
// Report), built by episode-panel.js. It is the in-app detail surface — the
// per-row details chevron and the URL-addressable in-app panel it replaced were
// retired in #2139.
//
// The shade is NOT URL-addressable — raising it pushes a single history entry
// carrying only a flag (no URL change), so the browser / Android back gesture
// lowers it while inbound /episode/<jobId> deep links still resolve to the
// standalone page. The shade follows playback: when the queue auto-advances (or the user
// plays another row) it re-renders to the new focused article.
let $shade = null;
let _shade = null;
// The page <header> (wordmark + cog), so the shade can sit just below it.
let $header = null;
// The article id the shade is currently showing — its focus cursor, null when
// the shade is lowered (or mid-lower). It guards refreshShadeFocus on two fronts:
// an unchanged focus is a no-op (so a pause/resume of the same track — both
// reachable on the docked transport that floats above the shade — never re-opens
// it and wipes a half-typed Report or the transcript scroll), and a null cursor
// means "lowering", so an auto-advance landing during the close slide (isOpen()
// stays true until it finishes) can't resurrect the shade.
let _shadeFocusId = null;
// True while the shade is being lifted under the finger from the docked transport
// (the interactive raise, #2137) — between the first onExpandMove and the
// commit/cancel that ends the drag. Distinguishes a live raise (settle / retract)
// from a tap or flick (the instant toggleShade path).
let _raising = false;

function ensureShade() {
  if (_shade || !$shade) return _shade;
  _shade = createEpisodePanel({
    host: $shade,
    // Lowering is the raise in reverse: unwind the pushed entry and let the
    // popstate handler do the hiding, so the lower control and the back gesture
    // stay one code path.
    onClose: () => history.back(),
    onShare: shareFocusedEpisode,
    onReport: submitEpisodeReport,
    // Roam the timeline (#2138): the shade's prev/next controls and a touch
    // swipe move the player's consumption-neutral browse cursor; we then
    // re-render the shade to the new focus. No history entry — cursor movement
    // never touches history (only the raise/lower does).
    onNavigate: navigateShade,
    // Carousel peek (#2227): resolve the adjacent episode's details so a swipe can
    // pre-render the destination panel and slide it in under the finger — without
    // moving the focus or fetching its transcript (that waits until it lands).
    onPeek: peekShade,
    // (#2232) Hand the peek the destination transcript when we've already cached
    // it (we preload neighbours), so it shows real content during the swipe
    // instead of the loading placeholder. A synchronous cache read; null when
    // uncached.
    onPeekTranscript: peekTranscriptContent,
    // (#2236) Tap-to-seek: a tapped read-along sentence seeks the shared queue
    // player to its start. For the playing/paused track this is an in-place seek
    // (the highlight + scroll re-anchor off the resulting flagged position event,
    // #2235); for a browsed-but-not-playing episode it starts that row there.
    onSeek: (details, positionSec) => queueHost.player()?.seekTo(details.jobId, positionSec),
    // (#2137 follow-up) Hold the read-along auto-scroll off while the transport
    // scrubber is being dragged, the same as for a shade drag: a scrub emits a
    // per-move `seek:true` position event that would otherwise re-anchor the
    // transcript every frame. The scrub state lives on the player; app.js owns both,
    // so it bridges the signal in.
    isScrubbing: () => queueHost.player()?.isScrubbing() ?? false,
  });
  return _shade;
}

// The queue row the player has focused (playing or idle-cued), or null. The
// host's rendered rows are the source of truth for the off-href extras — favicon,
// failure copy, detail metadata — which episodeDetailsForRow threads in.
function focusedItem() {
  return queueHost.focusedItem();
}

// Sit the shade just below the page header so the pinned wordmark (and cog) stay
// visible above it — it reads as a sheet over the home page, not a full takeover,
// and the header doubles as a tap-to-lower escape. Measured live so it tracks the
// real header bottom across breakpoints, the iOS safe area and the install
// banner; falls back to full-height (top:0, the CSS default) when there's no
// header to sit under (tests).
function positionShadeBelowHeader() {
  if (!$shade) return;
  const bottom = $header?.getBoundingClientRect?.().bottom ?? 0;
  $shade.style.top = bottom > 0 ? `${Math.round(bottom)}px` : '';
}

// Render the shade for the current focus (no history push) — shared by the raise
// gesture and a forward popstate. Returns false when nothing playable is focused
// (a cold or all-failed queue, or a row carrying no details href).
function openShade() {
  const shade = ensureShade();
  if (!shade) return false;
  const item = focusedItem();
  if (!item?.href) return false;
  const details = episodeDetailsForRow(item, item.href);
  // Set the top before unhiding so the rise animation plays from the right place.
  positionShadeBelowHeader();
  shade.open(details);
  loadEpisodeTranscript(shade, details);
  // Flip the docked player into its expanded footer form (#2137): it drops its
  // now-duplicated title row and sits flush beneath the sheet body, so the
  // two read as one expanding player.
  queueHost.player()?.setExpanded(true);
  _shadeFocusId = item.id;
  syncShadeNav();
  return true;
}

// Reflect the player's timeline ends on the shade's prev/next controls (#2138)
// so the cursor can't be stepped off either edge of the queue/history.
function syncShadeNav() {
  const player = queueHost.player();
  if (_shade && player) _shade.setNavigable(player.navigable());
}

// Drive the player's consumption-neutral browse cursor from the shade's
// navigation intent (#2138) and re-render the shade to the new focus. No
// history entry — cursor movement never touches history (only the shade
// raise/lower does, #2137).
function navigateShade(direction) {
  const player = queueHost.player();
  if (!player) return;
  // Don't roam away from the article being reported (#reading-shade-report-ux) —
  // a re-render to a neighbour would silently wipe a half-typed Report. The panel
  // already inerts its own prev/next + swipe while the form is open; this covers
  // the arrow-key path (app.js keydown) that also lands here.
  if (_shade?.isReportOpen?.()) return;
  player.navigate(direction);
  refreshShadeFocus();
}

// Resolve the adjacent episode's details for the carousel peek (#2227) — the
// panel pre-renders this as the finger drags so the destination slides in
// alongside the current one. peek() reads the neighbour WITHOUT moving the browse
// cursor (no transport re-render, no consume); the row (the table) carries the
// off-href extras episodeDetailsForRow needs. Returns null at a disabled timeline
// end (nothing adjacent), where the swipe keeps its single-panel rubber-band. The
// transcript is NOT fetched here — only on landing, via navigateShade.
function peekShade(direction) {
  const id = queueHost.player()?.peek(direction);
  if (!id) return null;
  const item = queueHost.item(id);
  if (!item?.href) return null;
  return episodeDetailsForRow(item, item.href);
}

// The player's expand control toggles the shade: lower an open one, else raise
// it for the focused row. The docked transport floats above the open shade
// (z-index 100 > 90 at every width), so the control stays reachable — tapping it
// again lowers, as the shade's own grabber and the back gesture do.
// Lowering routes through history.back; raising pushes the one history entry
// (URL unchanged — the shade is not URL-addressable) so back lowers it. Both
// guard on isOpen(), so a double-tap can't stack two entries.
function toggleShade() {
  if (_shade?.isOpen()) { history.back(); return; }
  if (!openShade()) return;
  history.pushState({ psShade: true }, '');
}

// Live raise (#2137): the docked transport's upward drag lifts the shade under
// the finger. The first move reveals the shade (beginRaise) for the focused row,
// then each move tracks the finger — `clientY` (the pointer's viewport Y) pins the
// sheet top to it so it appears right where the finger left the docked bar. A
// drag with nothing playable to show is a no-op — the gesture simply does
// nothing.
function dragRaiseShade(upPx, clientY) {
  if (!_raising) {
    // Don't hijack an already-open shade (a tap raised it) with a stray drag.
    if (_shade?.isOpen()) return;
    const shade = ensureShade();
    if (!shade) return;
    const item = focusedItem();
    if (!item?.href) return;
    const details = episodeDetailsForRow(item, item.href);
    positionShadeBelowHeader();
    shade.beginRaise(details);
    loadEpisodeTranscript(shade, details);
    queueHost.player()?.setExpanded(true);
    _shadeFocusId = item.id;
    syncShadeNav();
    _raising = true;
  }
  _shade?.dragRaise(upPx, clientY);
}

// The upward drag was released past the threshold: settle the shade open and push
// the one history entry (URL unchanged — the shade is not URL-addressable) so back
// lowers it, exactly as the tap-raise (toggleShade) does.
function commitRaiseShade() {
  if (!_raising) return;
  _raising = false;
  _shade?.settleRaise();
  history.pushState({ psShade: true }, '');
}

// The upward drag fell short (or was cancelled): retract the shade back down and
// unwind the docked player / browse state, the mirror of lowerShade but without a
// history entry (the raise never committed one).
function cancelRaiseShade() {
  if (!_raising) return;
  _raising = false;
  _shadeFocusId = null;
  queueHost.player()?.endBrowse();
  queueHost.player()?.setExpanded(false);
  _shade?.cancelRaise();
}

function lowerShade() {
  if (!_shade?.isOpen()) return;
  // Drop the focus cursor up front so an auto-advance landing during the close
  // slide (isOpen() stays true until it finishes) can't resurrect the shade.
  _shadeFocusId = null;
  // Drop the player's browse cursor too (#2138): the lens is closing, so focus
  // falls back to the playing / cued row (a track paused while browsing stays
  // paused, resumable from the docked transport).
  queueHost.player()?.endBrowse();
  // Collapse the docked player back to its mini form (title row + controls).
  queueHost.player()?.setExpanded(false);
  _shade.close();
}

// Keep an open shade pointed at the focused article as playback advances. A
// transient no-focus gap (e.g. a poll refresh between tracks) holds the last
// article rather than blanking out; the deliberate end-of-queue case lowers the
// shade outright (handleQueuePlayerEvent, on 'queue-end'). An unchanged focus is
// a no-op — so a pause/resume of the same track never re-renders the shade and
// wipes a half-typed Report or the transcript scroll.
function refreshShadeFocus() {
  if (!_shade?.isOpen() || _shadeFocusId === null) return;
  // Hold the focus while a Report is open (#reading-shade-report-ux): re-rendering
  // to the newly-focused article would wipe the half-typed form. The report is
  // about the article the reader opened it on, so the shade stays there until they
  // finish or discard it; the docked player keeps advancing the audio underneath.
  if (_shade.isReportOpen?.()) return;
  const item = focusedItem();
  if (!item?.href || item.id === _shadeFocusId) return;
  const details = episodeDetailsForRow(item, item.href);
  _shade.open(details);
  loadEpisodeTranscript(_shade, details);
  _shadeFocusId = item.id;
  syncShadeNav();
}

// Share the focused article's standalone /episode/<jobId> link (#2137). The
// shade never moves the address bar, so the link is built from the focused row's
// relative /episode/<jobId> href (the same durable page the feed points at),
// resolved to an absolute URL — never from the unchanged location.href.
async function shareFocusedEpisode() {
  const item = focusedItem();
  if (!item?.href) { showToast('Nothing to share', 'error'); return; }
  // Record the share activation (#2353) and mint a fresh ref on the link before
  // handing it to the OS share sheet / clipboard. item.id is the /episode jobId.
  const shareUrl = beginShare({ url: new URL(item.href, location.origin).href, jobId: item.id });
  await shareLink(shareUrl, episodeShareData(item.title));
}

// A save made from another surface (the toolbar popup, a context-menu link)
// can't reach this page directly, so the extension's content script
// (sync-token.js) re-dispatches the background's QUEUE_UPDATED as
// QUEUE_UPDATED_EVENT here. Refetch at once — the same hook the paste card fires
// after its own save (#1989) — so a queue parked open in this tab shows the new
// row immediately instead of waiting out the poll interval (≤60s).
// The refetch no-ops cleanly when signed out or before the list mounts.
window.addEventListener(QUEUE_UPDATED_EVENT, () => { queueHost.refetch().catch(() => {}); });

// ---------------------------------------------------------------------------
// /me fetch — the single account-info read (the install-banner gate + usage
// strip). Returns the parsed body, or null when there's no usable session.
// Throws an Error with `.status` on a non-2xx response (see shared/api.js).
// ---------------------------------------------------------------------------
async function fetchMe() {
  if (!isLoggedIn()) return null;
  const token = await getToken();
  if (!token) return null;
  const res = await apiRequest('/api/me', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.json();
}

// The install-prompt audience (ADR #1679 / #2093 / #1680): an "established" user
// has saved ≥ 1 article through an identified client, which leaves a non-empty
// per-client last-seen map on /me (`clients`, ADR #1627 §6 — the same signal
// me.ts notes drives the install prompt). Deliberately reads `clients`, never the
// podcast-app-subscription flag, so the audience stays decoupled from subscription.
function isEstablishedUser(data) {
  return Object.keys(data?.clients ?? {}).length > 0;
}
// Apply both halves of the audience gate from a /me-shaped object: established
// (saved) and `setUpToListen` (listened in-app OR subscribed in a podcast app,
// #2199/#2200). The resolver ANDs the two.
function applyInstallAudience(data) {
  _established = isEstablishedUser(data);
  _setUpToListen = Boolean(data?.setUpToListen);
}

// ---------------------------------------------------------------------------
// Fetch account info — drives the PWA install-banner gate (#1679 / #2093) and
// the Save card's out-of-quota panel.  Best-effort: a failure here doesn't
// block the rest of the page.
// ---------------------------------------------------------------------------
async function fetchAccountInfo() {
  try {
    const data = await fetchMe();
    if (!data) return;
    // The pack door, now that we know who is asking (#3817). The import-time
    // warm-up in shell.js ran before any tier was known, so it could only
    // price the packs no `tiers` list withdraws from anyone; this is where
    // the eligibility filter /settings renders its card through
    // (`packsForTier`) finally has a tier to apply. Fire-and-forget — the
    // latch is what the wall reads, and it only ever gains a door.
    //
    // A row whose tier never arrived is the FREE row, which is how every
    // pack-card reader in settings.js resolves it (`data?.tier ?? 'free'`).
    // Passing the absent value through would drop back to the tier-blind set
    // permanently — nothing re-warms after this — and the wall would name
    // nothing where Settings renders a card (#3416).
    warmPackDoor(data.tier ?? 'free');
    // Cache the feed ID for /settings (it owns the read once open) so a
    // returning user doesn't briefly see the previous account's feed URL.
    if (data.feedId) safeStorage.setItem(LS_FEED_ID, data.feedId);
    // The client install prompt (ADR #1679 / #1680) surfaces only for an
    // established user (saved ≥ 1 article — a non-empty /me `clients` map) who is
    // also set up to listen (/me `setUpToListen`), decoupled from podcast-app
    // subscription. The resolver re-checks the full audience + surface gate, so a
    // no-longer-qualifying user or an unsupported surface resolves to null → hidden.
    applyInstallAudience(data);
    showInstallBanner();
    renderSaveQuota(data);
  } catch (err) {
    // /me failed — the Save card simply keeps its current state. 4xx is silent; log the rest.
    const status = err?.status;
    if (typeof status === 'number' && status >= 400 && status < 500) return;
    console.warn('Failed to fetch account info', err);
  }
}

// A resumed page re-reads /me (clients/web/public/account-refresh.js).
const refreshAccountInfo = refreshOnResume(fetchAccountInfo);

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------
document.addEventListener('DOMContentLoaded', async () => {
  $signinCard       = document.getElementById('signin-card');
  $settingsLink     = document.getElementById('settings-link');

  $takeover         = document.getElementById('takeover');
  $takeoverUrl      = document.getElementById('takeover-url');
  $takeoverStatus     = document.getElementById('takeover-status');
  $takeoverStatusText = document.getElementById('takeover-status-text');
  $takeoverErrorText = document.getElementById('takeover-error-text');
  $takeoverQuotaText = document.getElementById('takeover-quota-text');

  $pasteCard        = document.getElementById('paste-card');
  $pasteInput       = document.getElementById('paste-input');
  $pasteSubmit      = document.getElementById('paste-submit');
  $pasteInputError  = document.getElementById('paste-input-error');
  $pasteErrorText   = document.getElementById('paste-error-text');
  $pasteBlockedText = document.getElementById('paste-blocked-text');

  $saveSample      = document.getElementById('save-sample');
  $saveSampleBtn   = document.getElementById('save-sample-btn');

  $installHowto        = document.getElementById('install-howto');
  $installHowtoDone    = document.getElementById('install-howto-done');

  $episodesCard = document.getElementById('episodes-card');
  $episodesList = document.getElementById('episodes-list');
  queueHost.mount($episodesList);
  $shade = document.getElementById('now-playing-shade');
  $header = document.querySelector('header');
  // While the shade is up, the still-visible wordmark lowers it (rather than
  // navigating home and tearing down playback); closed, it stays the home link.
  document.querySelector('.header-home')?.addEventListener('click', (e) => {
    if (_shade?.isOpen()) { e.preventDefault(); history.back(); }
  });

  // Mount the shared auth UI (sign-in / sign-up / verify / forgot / reset
  // panels) into the signin card and wire up all form handlers.
  initAuthUI({
    container: $signinCard,
    showToast,
    onSignedIn: () => {
      // New user session: drop the previous account's cached feedId so
      // /settings doesn't briefly render the previous account's URL, and re-arm
      // the install-banner audience so this account re-earns it via its own /me.
      safeStorage.removeItem(LS_FEED_ID);
      resetPerAccountState();
      setAuthHeader(null);
      const pending = readPendingSubmission();
      if (pending) {
        clearPending();
        applyTakeover(pending);
        queueTakeover(pending).catch(console.error);
      }
      // Load account info for the install-banner gate and the Save card's
      // out-of-quota panel.
      fetchAccountInfo().catch(console.error);
      // A same-session sign-in doesn't reload the page, so the pre-paint flags
      // never ran — lead with the queue layout + loader here too while the
      // fresh account's first episodes fetch is in flight.
      queueHost.showLoading();
      queueHost.refetch().catch(console.error);
      // An in-place sign-in: reconcile the client-install how-to captured while
      // the user was still logged out — shown, or deferred behind the sample
      // nudge per the funnel (the OAuth path is handled by the post-auth boot
      // step, which runs on the reloaded page).
      syncInstallHowto();
    },
  });

  // Boot-time gate for the reloaded-while-signed-in case; auth changes and
  // pageshow re-run it via the module-level registrations above.
  syncBridgeDebugPanel();

  document.getElementById('takeover-done-btn')?.addEventListener('click', dismiss);
  // Tapping the empty backdrop outside the card dismisses too (success only —
  // see handleTakeoverBackdropClick), so the bottom of the screen is a one-
  // handed-reachable target without moving the button.
  $takeover?.addEventListener('click', handleTakeoverBackdropClick);
  document.getElementById('takeover-quota-dismiss-btn')?.addEventListener('click', dismiss);
  // "Manage subscription": in the extension handoff window, break out to a
  // real main-window tab rather than navigating the chromeless popup in place.
  document.getElementById('takeover-quota-link')?.addEventListener('click', (e) => {
    if (handleManageSubscription()) e.preventDefault();
  });
  // Header wordmark → home, cog → settings.  Gated (aria-disabled) until the
  // save settles; once live, break out of the chromeless popup like the quota
  // link, else let the <a href> navigate.
  for (const [id, event] of [
    ['takeover-home', OPEN_APP_EVENTS.home],
    ['takeover-settings', OPEN_APP_EVENTS.settings],
  ]) {
    document.getElementById(id)?.addEventListener('click', (e) => {
      if (e.currentTarget.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
      if (handleTakeoverNav(event)) e.preventDefault();
    });
  }
  // Error dismiss mirrors the quota path: dismiss() runs the multi-strategy
  // close so an extension-bounce popup is closed (not just hidden), falling
  // back to navigating to / in a plain tab.
  document.getElementById('takeover-error-dismiss-btn')?.addEventListener('click', dismiss);
  document.getElementById('takeover-retry-btn')?.addEventListener('click', () => {
    if (_takeoverSubmission) queueTakeover(_takeoverSubmission).catch(console.error);
  });

  document.getElementById('paste-submit')?.addEventListener('click', () => {
    submitPaste().catch(console.error);
  });
  // Editing dismisses a lingering inline / save error (#2019); shared with the
  // "Try a sample" fill via dismissPasteInputErrors. A real keystroke also
  // retires the new-user Save-button nudge — but only the user's own typing,
  // not the programmatic fill (which sets .value without firing 'input').
  $pasteInput?.addEventListener('input', () => {
    dismissPasteInputErrors();
    clearSampleNudge();
  });

  // New-user sample (ADR #2089 §4): "Try a sample" drops the curated demo into
  // the paste box so the user presses Save themselves. the queue host's onEpisodes toggles
  // the line with whether the user has saved a real article yet.
  $saveSampleBtn?.addEventListener('click', useSample);

  // Client-install how-to: "Got it" is its one dismissal (the latch is consumed
  // on show, so dismissal is final). Capture any signal already on the URL at
  // load (fresh-tab install, or a reload carrying the fragment) into the
  // two-part latch now — before any sign-in round-trip — so it survives to the
  // signed-in page; syncInstallHowto() (after auth settles, below) shows or
  // defers it per the funnel.
  $installHowtoDone?.addEventListener('click', dismissInstallHowto);
  captureInstallSignal();

  // Register auth state listener BEFORE processOAuthCallback so auth state
  // transitions during the callback are not missed.
  onAuthChange(() => { updateAuthUI().catch(console.error); });

  // Attribute an acquisition-driven sign-up — a share (#2353, gate ADR #2336)
  // or a campaign (#2915). onAuthChange fires on EVERY auth transition — a
  // silent token refresh and a returning user's sign-in included — so gate on a
  // genuine new account (a logged-out→logged-in edge on a device that had never
  // signed in before) via shouldAttributeSignup; crediting a refresh or a
  // returning sign-in would inflate the very acquisition metric this gate
  // informs. The returning flag is read NOW, before any sign-in in this load
  // sets it. recordAttributedSignup then no-ops unless one of the two chains has
  // something pending, and ranks them (share wins, campaign rides along) so the
  // two bridges can never both fire for one sign-up. Registered before
  // processOAuthCallback so an OAuth sign-up's transition is caught too.
  let referralWasLoggedIn = isLoggedIn();
  const referralDeviceWasReturning = isReturningUser();
  onAuthChange(() => {
    const nowLoggedIn = isLoggedIn();
    const attributable = shouldAttributeSignup({
      wasLoggedIn: referralWasLoggedIn,
      nowLoggedIn,
      wasReturningUser: referralDeviceWasReturning,
    });
    referralWasLoggedIn = nowLoggedIn;
    if (attributable) recordAttributedSignup();
  });

  // Process OAuth callback / account-linking retry (shared logic in auth-ui).
  // beforeCallback clears the new session's cached feed-ID (so a different
  // account signing in via OAuth on a shared device doesn't show the previous
  // account's feed URL on /settings) and re-arms the install-banner audience so
  // it re-earns it via its own /me (#2093).
  await processOAuthCallback({
    beforeCallback: () => {
      safeStorage.removeItem(LS_FEED_ID);
      resetPerAccountState();
    },
  });

  // Logged-out boot with a stashed share URL (cold-open after pre-paint
  // detected SS_PENDING_URL, or an OAuth callback that didn't complete a
  // sign-in): pre-set the auth header so the card renders with context as
  // soon as updateAuthUI unhides it.  Runs after processOAuthCallback so a
  // successful sign-in skips this no-op.
  if (!isLoggedIn()) {
    const pending = readPendingSubmission();
    if (pending) setAuthHeader(pendingSubmissionHeader(pending));
  }

  await updateAuthUI();

  // Auth has settled (processOAuthCallback + updateAuthUI ran), so reconcile the
  // client-install how-to if a signal was captured — including one that rode the
  // latch through an OAuth redirect. It shows now, or defers behind the sample
  // nudge until the first save (the queue host's onEpisodes re-runs it). A signal captured
  // while still logged out is left for onSignedIn.
  syncInstallHowto();

  handleShareTarget();

  // Greet a fresh, logged-out arrival with the sign-up panel instead of the
  // bare sign-in form (no-op for returning users and pending-save boots).
  maybeShowFirstRunWelcome();

  fetchAccountInfo().catch(console.error);

  // Populate the main-page episode list (#1989) and keep it live — the
  // visibility-gated poller (#1990, ADR #1987) polls immediately, then on
  // an interval while the tab is visible and on visibility/focus regain.
  // Not on a takeover boot (share-target / extension handoff): the list is
  // covered by the overlay for the popup's whole lifetime, so it gets the
  // single save-time refetch only (queueHost.refetch's fallback) — the poll
  // loop would otherwise run behind the overlay until the window closes.
  // hideTakeover() starts the loop if the user lands on the lean view.
  // Re-assert the queue-loading layout (the pre-paint set it, but storage
  // access there can throw) so the first poll lands on a queue skeleton, not
  // the empty Save hero.
  if (!$takeover || $takeover.hidden) {
    queueHost.showLoading();
    queueHost.start();
  }

  // Install banner button handlers — the action varies per client (ADR #1679).
  document.getElementById('install-btn')?.addEventListener('click', async () => {
    const rec = _activeInstall;
    if (!rec) return;
    if (rec.action === 'open-url') {
      // The extension installs from its store listing in a new tab. We can't see
      // whether they complete it, so suppress the prompt for THIS session only
      // (not a permanent dismiss): an actual install is detected via the install
      // signal / ext-presence marker and clears it for good, while a click that
      // doesn't convert re-offers in a later session. The ✕ button is the
      // permanent "no thanks". (#1680)
      window.open(rec.url, '_blank', 'noopener');
      sessionStorage.setItem(SS_INSTALL_SUPPRESS, rec.client);
      hideInstallBanner();
      return;
    }
    // PWA: fire the deferred beforeinstallprompt.
    if (!deferredInstallPrompt) return;
    deferredInstallPrompt.prompt();
    const { outcome } = await deferredInstallPrompt.userChoice;
    deferredInstallPrompt = null;
    if (outcome === 'accepted') hideInstallBanner();
  });

  document.getElementById('install-dismiss')?.addEventListener('click', dismissInstall);

  // Surface whatever the resolver recommends now that the DOM is ready (a
  // beforeinstallprompt may have fired pre-DOMContentLoaded; the desktop
  // extension branch has no prompt to wait on).
  showInstallBanner();

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('./sw.js').catch((err) => {
      console.warn('SW registration failed', err);
    });
  }
});

// ---------------------------------------------------------------------------
// Internal exports for testing
// ---------------------------------------------------------------------------
export function _resetTwaBridge() {
  _twaPort = null;
}

export { dbgDetectBridge as _dbgDetectBridge };
export { dbgUpdatePlayBilling as _dbgUpdatePlayBilling };

/** Test seam: the panel's log ring and its change memo are document-lifetime,
 *  so a suite asserting what a refresh logs has to start from empty. */
export function _resetDbgLog() {
  _dbgLogLines.length = 0;
  _dbgPlayUnpricedLogged = null;
}
export { redactConfigSummary as _redactConfigSummary, dbgConfigSummary as _dbgConfigSummary };

export function _initDomRefs(refs) {
  if (refs.toastStack) _setToastStack(refs.toastStack);
  $signinCard       = refs.signinCard       ?? null;
  $settingsLink     = refs.settingsLink     ?? null;
  $takeover         = refs.takeover         ?? null;
  $takeoverUrl      = refs.takeoverUrl      ?? null;
  $takeoverStatus     = refs.takeoverStatus     ?? null;
  $takeoverStatusText = refs.takeoverStatusText ?? null;
  $takeoverErrorText = refs.takeoverErrorText ?? null;
  $takeoverQuotaText = refs.takeoverQuotaText ?? null;
  $pasteCard        = refs.pasteCard        ?? null;
  $pasteInput       = refs.pasteInput       ?? null;
  $pasteSubmit      = refs.pasteSubmit      ?? null;
  $pasteInputError  = refs.pasteInputError  ?? null;
  $pasteErrorText   = refs.pasteErrorText   ?? null;
  $pasteBlockedText = refs.pasteBlockedText ?? null;
  $saveSample      = refs.saveSample      ?? null;
  $saveSampleBtn   = refs.saveSampleBtn   ?? null;
  $installHowto       = refs.installHowto       ?? null;
  $installHowtoDone    = refs.installHowtoDone    ?? null;
  $episodesCard     = refs.episodesCard     ?? null;
  $episodesList     = refs.episodesList     ?? null;
  $shade            = refs.shade            ?? null;
  $header           = refs.header           ?? null;
  // A fresh list element knows nothing of past renders — the host tears the
  // old player down so it remounts into the new element and the first fetch
  // paints it.
  queueHost.mount($episodesList);
  // Likewise the now-playing shade (#2137): rebuild it into the new host.
  _shade = null;
  _shadeFocusId = null;
  _resetTranscriptCache(); // a fresh mount starts with no other queue's transcripts
}
export { _setRetryDelays as _setApiRetryDelays };
export { queueArticle as _queueArticle, updateAuthUI as _updateAuthUI,
         handleShareTarget as _handleShareTarget,
         maybeShowFirstRunWelcome as _maybeShowFirstRunWelcome,
         handleNextRedirect as _handleNextRedirect,
         SS_PENDING_URL as _SS_PENDING_URL,
         SS_PENDING_TEXT as _SS_PENDING_TEXT,
         SS_PENDING_SHARE as _SS_PENDING_SHARE,
         SS_CLOSE_STRATEGY as _SS_CLOSE_STRATEGY,
         SS_NEXT_REDIRECT as _SS_NEXT_REDIRECT,
         showTakeover as _showTakeover,
         hideTakeover as _hideTakeover,
         queueTakeoverUrl as _queueTakeoverUrl,
         setTakeoverState as _setTakeoverState,
         dismiss as _dismiss,
         handleTakeoverBackdropClick as _handleTakeoverBackdropClick,
         handleManageSubscription as _handleManageSubscription,
         handleTakeoverNav as _handleTakeoverNav,
         classifyWebSaveError as _classifyWebSaveError,
         saveErrorTextForKind as _saveErrorTextForKind,
         submitPaste as _submitPaste,
         showInstallBanner as _showInstallBanner,
         captureInstallSignal as _captureInstallSignal,
         syncInstallHowto as _syncInstallHowto,
         dismissInstallHowto as _dismissInstallHowto,
         extensionBrowser as _extensionBrowser,
         SS_INSTALL_SIGNAL as _SS_INSTALL_SIGNAL,
         LS_INSTALL_HOWTO_OWED as _LS_INSTALL_HOWTO_OWED,
         setHasSavedArticleForTest as _setHasSavedArticle,
         useSample as _useSample,
         renderSaveQuota as _renderSaveQuota,
         resetPerAccountState as _resetPerAccountState,
         fetchAccountInfo as _fetchAccountInfo,
         _resetTranscriptCache,
         _transcriptCached };

// Test hooks: the page's queue host, driven through its own interface. The
// suites that load the whole page reach the queue through these; the host's
// own suite (test/queue-host.test.ts) mounts it without the page.
export const _refreshEpisodes = () => queueHost.refresh();
export const _startEpisodesPolling = (intervals) => queueHost.start(intervals);
export const _stopEpisodesPolling = () => queueHost.stop();
export const _showQueueLoading = () => queueHost.showLoading();
export const _setQueueAudioFactory = (factory) => queueHost.setAudioFactory(factory);

// Test hook for the install-prompt audience (ADR #1679 / #2093 / #1680): apply
// both gate halves from a /me-shaped object, as fetchAccountInfo does.
export { applyInstallAudience as _applyInstallAudience };

// Test hook: stand in a deferred beforeinstallprompt (the PWA's "available ∧ not
// installed here" signal) without dispatching a real browser event.
export function _setDeferredInstallPrompt(prompt) { deferredInstallPrompt = prompt; }
// Drive the native-app "installed here" fact directly in tests, sidestepping the
// async getInstalledRelatedApps probe (#1644). Also resets the one-shot guard so
// the probe can run again if the test re-enables the API.
export function _setNativeAppInstalled(v) { _nativeAppInstalled = v; _nativeAppInstalledChecked = false; }
// Test hook: drive the load-time TWA confirmation from a document.referrer value
// (our own Android app stamps it on the launch navigation), the same call app.js
// makes at boot — proves the "get the app" nudge is suppressed by the referrer
// alone, without the CCT bridge ever confirming, and latched off from then on.
export { confirmTwaFromReferrer as _confirmTwaFromReferrer };

// Test hook: clear the cached /me usage snapshot so a blocked-quota state set by
// one test doesn't leak into the next (the paste card reads `_lastUsageData` to
// decide its resting state).
export function _resetUsageState() { _lastUsageData = null; }

// Re-export auth-ui functions for test compatibility
export { configureAuthUI as _configureAuthUI,
         handleSignInSubmit as _handleSignInSubmit,
         validatePassword as _validatePassword,
         handleSignUp as _handleSignUp,
         handleVerifyCode as _handleVerifyCode,
         handleResendCode as _handleResendCode,
         showAuthPanel as _showAuthPanel,
         startResendCooldown as _startResendCooldown,
         handleForgotPassword as _handleForgotPassword,
         handleResetPassword as _handleResetPassword,
         handleResendResetCode as _handleResendResetCode,
         _clearResendTimer, _clearResendResetTimer,
         _resetSignUpState, _resetResetState };
